// increase in volume (repetition, sets), intensity
// development of key symptom: pain, headache, tension

import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import {
  Box,
  Button,
  Card,
  Grid,
  CardContent,
  CardHeader,
  Divider,
  useTheme,
  makeStyles,
  colors,
  Avatar,
  Paper,
  Chip,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Badge,
  IconButton,
  Popover
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DiscreteSliderAdmin from '../../treatmentSession/rehabPhaseProgressBar_symptomTracker';
import DiscreteSlider from '../../treatmentSession/rehabPhaseProgressBar';
import { Snackbar } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import BarChartIcon from '@material-ui/icons/BarChart';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import WhatshotIcon from '@material-ui/icons/Whatshot';
import StarIcon from '@material-ui/icons/Star';
import WeeklyActivityCalendar from './WeeklyActivityCalendar';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(0,45,85,0.08)',
    background: '#f7faff',
    marginBottom: 0,
    padding: 0,
  },
  cardHeaderRoot: {
    alignItems: 'center',
  },
  avatar: {
    backgroundColor: '#6a8caf',
    height: 'clamp(20px, 2.2vw, 36px)',
    width: 'clamp(20px, 2.2vw, 36px)',
  },
  cardHeaderTitle: {
    color: "#002D55",
    fontWeight: "bold",
    fontSize: 'clamp(15px, 1.4vw, 26px)',
  },
  ChangeButton: {
    fontWeight: 'bold',
    color: '#FFF',
    background: '#002D55',
    borderRadius: 16,
    paddingLeft: 32,
    paddingRight: 32,
    fontSize: 'clamp(14px, 1.15vw, 22px)',
    boxShadow: '0 2px 8px rgba(0,45,85,0.12)',
    letterSpacing: 0.5,
    textTransform: 'none',
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: '#fb741a',
    },
    '&:active': {
      backgroundColor: '#fb741a',
      boxShadow: 'none',
    },
  },
  pairContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: "0%",
  },
  leftPane: {
    width: '100%',
  },
  rightPane: {
    width: '100%',
  },
  weeklyContainer: {
    marginTop: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginLeft: 0,
    boxSizing: 'border-box',
    // grid / container will control outer width; ensure internal content fills available space
    flex: '1 1 100%',
    width: '100%',
  },

  adherenceBox: {
    width: '90%',
  },
  weekdayColsWrapper: {
    display: 'flex',
    gap: 0, // reduced gap to tighten column spacing
    justifyContent: 'center', // center the weekday columns horizontally
    alignItems: 'center', // center contents vertically within the wrapper
    width: '100%', // ensure wrapper spans available container width so justifyContent works
    margin: '0 auto', // center wrapper within any parent that restricts width
    boxSizing: 'border-box',
    },
  overviewContent: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch'
    }
  },
  metricsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flex: '1 1 320px',
    minWidth: 220,
    boxSizing: 'border-box'
  },
  weekdayCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    // narrower columns to reduce total width
    flex: '0 0 48px',
    minWidth: 44,
    width: 48,
    [theme.breakpoints.between && theme.breakpoints.between('sm','md')]: {
      // slightly narrower on medium screens
      flex: '0 0 44px',
      minWidth: 40,
      width: 44
    },
    [theme.breakpoints.down('sm')]: {
      // on small screens allow wrapping: make columns take a percentage so they wrap into rows
      flex: '0 0 30%',
      minWidth: '0',
      width: '30%'
    },
    [theme.breakpoints.down('xs')]: {
      // very small screens: two columns per row
      flex: '0 0 45%',
      width: '45%'
    }
  },
  // linear progress custom styles (top-level keys)
  linearTrack: {
    backgroundColor: '#f5f7f8',
  },
  linearBar: {
    backgroundColor: '#fb741a',
  },
  /* keep pairContainer stacked vertically so "Your motivation" appears above the chart */
}));

const ChartProgress = ({ className, improvements = null, baselineSelfRated = null, baselineSelfFunction = null, clinicianKpis = [], kpiHistory = {}, ...rest }) => {
  const useDemoData = !rest.treatmentId;
  const demoOrEmpty = (values) => (useDemoData ? values : []);

  // Data for other metrics
  const adherenceData = demoOrEmpty([90, 76, 80, 85, 77, 80, 90]);

  // ---- Build real data from kpiHistory if available, else use demo data ----
  const buildSeriesFromKpiHistory = (histData, questionIdentifier) => {
    const entry = histData && histData[questionIdentifier];
    if (!entry || !Array.isArray(entry.series) || entry.series.length < 2) return null;
    return entry.series.map((pt) => pt.numeric_value != null ? pt.numeric_value : null).filter(v => v != null);
  };

  const _realPainCurrent = buildSeriesFromKpiHistory(kpiHistory, 'SelfRatedPain_Current');
  const _realPainWorst = buildSeriesFromKpiHistory(kpiHistory, 'SelfRatedPain_AtWorst');
  const _realFrequency = buildSeriesFromKpiHistory(kpiHistory, 'SelfRatedPain_Frequency');
  const _realFunction = buildSeriesFromKpiHistory(kpiHistory, 'SelfRatedFunction_MainGoal') || buildSeriesFromKpiHistory(kpiHistory, 'SelfRatedFunction_Difficulty');

  // daily series expanded to 7 weeks (49 days) so weekly aggregation shows trends
  const painDailyData = _realPainCurrent || demoOrEmpty([
    80,78,82,79,81,80,77,
    72,70,71,69,68,70,73,
    62,60,61,59,58,60,63,
    52,50,51,49,48,50,53,
    42,40,41,39,38,40,43,
    32,30,31,29,28,30,33,
    22,20,21,19,18,20,23
  ]);
  const painLeisureData = demoOrEmpty([18, 5, 19, 27, 29, 19, 20]);
  const painWorstData = _realPainWorst || demoOrEmpty([
    95,93,96,94,95,95,92,
    90,88,89,87,86,88,90,
    85,83,84,82,81,83,85,
    78,76,77,75,74,76,78,
    70,68,69,67,66,68,70,
    62,60,61,59,58,60,62,
    52,50,51,49,48,50,52
  ]);
  const painFrequencyData = _realFrequency || demoOrEmpty([
    90,88,92,89,91,90,87,
    82,80,81,79,78,80,83,
    74,72,73,71,70,72,75,
    66,64,65,63,62,64,67,
    58,56,57,55,54,56,59,
    46,44,45,43,42,44,47,
    34,32,33,31,30,32,35
  ]);
  const npsData = demoOrEmpty([80, 50, 60, 65, 66, 70, 82]);

  // Calculations for each metric
  const adherenceChange = adherenceData[adherenceData.length - 1] - adherenceData[0];
  const adherencePercent = ((adherenceChange / adherenceData[0]) * 100).toFixed(1);

  const painDailyChange = painDailyData[0] - painDailyData[painDailyData.length - 1];
  const painDailyPercent = ((painDailyChange / painDailyData[0]) * 100).toFixed(1);

  const painLeisureChange = painLeisureData[0] - painLeisureData[painLeisureData.length - 1];
  const painLeisurePercent = ((painLeisureChange / painLeisureData[0]) * 100).toFixed(1);

  const npsChange = npsData[npsData.length - 1] - npsData[0];
  const npsPercent = ((npsChange / npsData[0]) * 100).toFixed(1);
  const classes = useStyles();
  const theme = useTheme();
  const chartRef = React.useRef(null);
  const [popoverAnchor, setPopoverAnchor] = React.useState(null);
  // If an `onStartFollowup` prop is provided, prefer that (it will replace the chart
  // with the questionnaire UI). Otherwise fall back to opening the popover.
  const handleOpenPopover = () => {
    if (typeof rest.onStartFollowup === 'function') {
      try {
        rest.onStartFollowup();
        return;
      } catch (err) {
        console.error('onStartFollowup callback failed:', err);
      }
    }
    window.location.href = '/FollowUpAssessment_questionaire';
  };
  const handleClosePopover = () => setPopoverAnchor(null);
  const popoverOpen = Boolean(popoverAnchor);
  // helper to resolve the underlying Chart.js instance from react-chartjs-2 ref
  const getChart = () => {
    const ref = chartRef.current;
    if (!ref) return null;
    // react-chartjs-2 v2 exposes chartInstance, v3+ usually exposes the Chart instance directly
    // some wrappers expose `chart` as well — check common properties
    return ref.chartInstance || ref.chart || ref;
  };

  // Extract relevant data for calculations
  const painRestData = demoOrEmpty([100, 55, 19, 27, 29, 19, 18]);
  const functionData = _realFunction || demoOrEmpty([
    18,20,22,19,21,20,23,
    28,31,29,30,32,30,33,
    38,40,41,39,42,40,43,
    48,50,51,49,52,50,53,
    58,60,62,59,61,60,63,
    68,70,72,69,71,70,73,
    78,80,82,79,81,80,83
  ]);

  // Calculate improvement in pain (rest)
  const painRestImprovement = painRestData[0] - painRestData[painRestData.length - 1];
  const painRestPercent = ((painRestImprovement / painRestData[0]) * 100).toFixed(1);

  // Prepare placeholders for self-reported function computations.
  // The actual series (`_funcSeriesForCalc`) prefers `weeklyFunction` but
  // `weeklyFunction` is built later in this component, so we'll resolve the
  // final values after `weeklyFunction` is available to avoid temporal-dead-zone
  // ReferenceErrors during render.
  let _funcSeriesForCalc = null;
  let functionImprovement = 0;
  let functionPercent = '0.0';

  // Helper to compute percent change from baseline to last and from baseline to last-window average
  const computePercentChange = (series, toAverageWindow = 1) => {
    if (!Array.isArray(series) || series.length < 1) return { overall: 0, window: 0 };
    const baseline = series[0] || 0;
    const last = series[series.length - 1] || 0;
    const overall = baseline === 0 ? 0 : ((last - baseline) / Math.abs(baseline)) * 100;

    const windowSize = Math.min(toAverageWindow, series.length);
    const startIdx = Math.max(0, series.length - windowSize);
    const windowSlice = series.slice(startIdx);
    const windowAvg = windowSlice.reduce((s, v) => s + v, 0) / (windowSlice.length || 1);
    const window = baseline === 0 ? 0 : ((windowAvg - baseline) / Math.abs(baseline)) * 100;

    return { overall, window };
  };

  // Compute effect sizes for function and pain (pain inverted: reduction is improvement)
  // Use last 14 days window when available; otherwise use available points
  const WINDOW_DAYS = 14;
  // placeholders for later computed values
  let funcChanges = { overall: 0, window: 0 };
  let funcBaseline = 0;
  const painChanges = computePercentChange(painDailyData, WINDOW_DAYS);
  // For pain we want improvement as baseline - value, so invert sign to show positive improvement
  const painOverallImprovement = -(painChanges.overall);
  const painWindowImprovement = -(painChanges.window);
  // 7-day window variant for recent (weekly) display
  const painChanges7 = computePercentChange(painDailyData, 7);
  const painWindowImprovement7 = -(painChanges7.window);

  // Compute for pain worst and frequency (reduce=improve -> invert sign)
  const worstChanges = computePercentChange(painWorstData, WINDOW_DAYS);
  const worstOverallImprovement = -(worstChanges.overall);
  const worstWindowImprovement = -(worstChanges.window);
  // 7-day window for worst-pain recent display
  const worstChanges7 = computePercentChange(painWorstData, 7);
  const worstWindowImprovement7 = -(worstChanges7.window);

  const freqChanges = computePercentChange(painFrequencyData, WINDOW_DAYS);
  const freqOverallImprovement = -(freqChanges.overall);
  const freqWindowImprovement = -(freqChanges.window);
  // Also compute a 7-day window for frequency-specific weekly display
  const freqChanges7 = computePercentChange(painFrequencyData, 7);
  const freqWindowImprovement7 = -(freqChanges7.window);

  // Helper for positive/negative coloring
  const getColor = (val) => val > 0 ? '#00e676' : '#e53935';

  // Color mapping for avg pain value (per exercise)
  // <30 -> green, 31-50 -> yellow, 51-65 -> #d84315, >65 -> #d81515ff
  const painValueColor = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return '#002D55';
    if (n < 36) return '#17aa2d';
  if (n <= 50) return '#f0b700';
    if (n <= 65) return '#d84315';
    return '#d81515ff';
  };

  // render a white-pill value with conditional color; if `denom` is provided the value will be
  // converted to a percentage (value/denom * 100) for color-decisions but the displayed value
  // remains the raw `value`. If `suffix` is provided it will be rendered after the pill in
  // the neutral color '#002D55' (this keeps e.g. '/7' or '/31 days' colored separately).
  // render a value with conditional color; if `denom` is provided the value will be
  // converted to a percentage (value/denom * 100) for color-decisions but the displayed value
  // remains the raw `value`. `invert` flips the color decision (useful for metrics where
  // higher is better, e.g. adherence or function). If `suffix` is provided it will be
  // rendered after the value in the neutral color '#002D55'.
  const renderPillValue = (value, { denom = null, suffix = null, invert = false, suffixColor = '#002D55' } = {}) => {
    const numeric = parseFloat(value);
    const pct = (denom && denom > 0) ? (numeric / denom) * 100 : numeric;
    const color = painValueColor(invert ? (100 - pct) : pct);
    return (
      <>
        <span style={{
          color,
          padding: '2px 8px',
          borderRadius: 8,
          display: 'inline-block'
        }}>{value}</span>
        {suffix ? <span style={{ color: suffixColor, marginLeft: 6 }}>{suffix}</span> : null}
      </>
    );
  };

  // For this view we only show function and pain (daily activities)
  const baseData = {
    labels: ['1 Aug', '2 Aug', '3 Aug', '4 Aug', '5 Aug', '6 Aug', '7 Aug'],
    datasets: [
      {
        label: 'Patient Specific Functional Scale (PSFS)',
        data: functionData,
        fill: true,
        backgroundColor: 'rgba(0,45,85,0.27)',
        borderColor: '#002D55',
        pointBackgroundColor: '#002D55',
        tension: 0.6,
      },
      {
        label: 'Pain (at best)',
        data: painDailyData,
        fill: true,
        backgroundColor: 'transparent',
        borderColor: '#fb741a',
        pointBackgroundColor: '#fb741a',
        tension: 0.2,
      },
      {
        label: 'Pain (at worst)',
        data: painWorstData,
        fill: true,
        backgroundColor: 'transparent',
        borderColor: '#d81515ff',
        pointBackgroundColor: '#d84315',
        tension: 0.4,
      },
      {
        label: 'Frequency of symptoms',
        data: painFrequencyData,
        fill: true,
        backgroundColor: 'transparent',
        borderColor: '#fff243ff',
        pointBackgroundColor: '#fff243ff',
        tension: 0.4,
      }
    ]
  };

  // week offset: 0 = current week, -1 = one week ago, +1 = next week
  const [weekOffset, setWeekOffset] = React.useState(0);

  const getWeekSlice = (series, offset) => {
    // Interpret offset relative to the most recent week: offset=0 -> latest 7 items,
    // offset=-1 -> previous week, offset=+1 -> next week (likely empty).
    const len = Array.isArray(series) ? series.length : 0;
    const start = len - 7 + (offset * 7);
    if (start < 0 || start >= len) return [];
    return series.slice(start, Math.min(start + 7, len));
  };

  const getWeekLabels = (offset) => {
    const end = new Date();
    end.setDate(end.getDate() + offset * 7);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    const opts = { month: 'short', day: 'numeric' };
    const labels = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      labels.push(d.toLocaleDateString(undefined, opts));
    }
    return labels;
  };

  // weekday short names for a given week (Mon, Tue, ...)
  const getWeekDayAbbrevs = (offset) => {
    const end = new Date();
    end.setDate(end.getDate() + offset * 7);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    const opts = { weekday: 'short' };
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d.toLocaleDateString(undefined, opts));
    }
    return days;
  };

  // Build an array of 7 objects for the WeeklyActivityCalendar component
  // Each item: { day: 'Mon', date: 20, sessions: 0|1|2|3 }
  const getWeekData = (offset = 0) => {
    const end = new Date();
    end.setDate(end.getDate() + offset * 7);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);

    const adherenceSlice = getWeekSlice(adherenceData, offset);
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const dayAbbrev = d.toLocaleDateString(undefined, { weekday: 'short' });
      const dateNum = d.getDate();
      const pct = (adherenceSlice && adherenceSlice[i]) ? adherenceSlice[i] : 0;
      // infer up to 3 sessions per day from percent (0-100 -> 0-3 sessions)
      const sessions = Math.max(0, Math.min(3, Math.round((pct / 100) * 3)));
      out.push({ day: dayAbbrev, date: dateNum, sessions });
    }
    return out;
  };

  // Show weekly aggregated datapoints (one point per week) and label them with ISO week numbers.
  // This aggregates daily series into weekly averages and displays the last WEEKS_TO_SHOW weeks.
  const WEEKS_TO_SHOW = 7;

  const getIsoWeekNumber = (d) => {
    // ISO week number (1-53)
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    // Set to nearest Thursday: current date + 4 - current day number
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    return weekNo;
  };

  const getWeeklyLabels = (weeks = WEEKS_TO_SHOW, offset = weekOffset) => {
    const labels = [];
    for (let i = 0; i < weeks; i++) {
      const d = new Date();
      // compute end-of-week date for each displayed week (oldest -> newest)
      d.setDate(d.getDate() - ((weeks - 1 - i) * 7) - (offset * 7));
      labels.push(`W${getIsoWeekNumber(d)}`);
    }
    return labels;
  };

  const aggregateToWeeks = (series, weeks = WEEKS_TO_SHOW, offset = weekOffset) => {
    const N = Array.isArray(series) ? series.length : 0;
    const out = [];
    const totalNeeded = weeks * 7;
    for (let i = 0; i < weeks; i++) {
      const start = N - totalNeeded - (offset * 7) + (i * 7);
      const s = Math.max(0, start);
      const e = Math.min(N, start + 7);
      const slice = series.slice(s, e);
      if (!slice.length) {
        out.push(null);
      } else {
        const avg = slice.reduce((a, b) => a + b, 0) / slice.length;
        out.push(Number.isFinite(avg) ? parseFloat(avg.toFixed(1)) : null);
      }
    }
    return out;
  };

  const weekLabels = getWeeklyLabels();
  // Determine ISO week number for the currently selected weekOffset
  const headerWeekNumber = (() => {
    const d = new Date();
    d.setDate(d.getDate() + weekOffset * 7);
    return getIsoWeekNumber(d);
  })();
  let weeklyFunction = aggregateToWeeks(functionData);
  let weeklyPainDaily = aggregateToWeeks(painDailyData);
  let weeklyPainWorst = aggregateToWeeks(painWorstData);
  let weeklyPainFrequency = aggregateToWeeks(painFrequencyData);

  

  

  const options = {
    responsive: true,
    spanGaps: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: theme.palette.background.default,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        enabled: true,
        titleColor: theme.palette.text.primary,
        padding: 12,
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: theme.palette.text.secondary, font: { size: 13 } }
      },
      y: {
        grid: { color: theme.palette.divider, borderDash: [4, 4] },
        ticks: { color: theme.palette.text.secondary, font: { size: 13 }, beginAtZero: true, min: 0 }
      }
    },
    elements: {
      line: { borderWidth: 3, tension: 0.3 },
      point: { radius: 4, hoverRadius: 6 }
    }
  };

  const [subGoals, setSubGoals] = React.useState([
    "Turn head freely without pain",
    "Sleep through the night without neck discomfort",
    "Return to sports or work without neck limitations"
  ]);
  const [subGoalValues, setSubGoalValues] = React.useState([0, 0, 0]);
  const [symptoms, setSymptoms] = React.useState([
    "Pain intensity (rest and activity)",
    "Stiffness or tension in neck",
    "Headache frequency"
  ]);
  const [symptomValues, setSymptomValues] = React.useState([0, 0, 0]);
  const [newSubGoal, setNewSubGoal] = React.useState("");
  const [newSymptom, setNewSymptom] = React.useState("");
  const [celebrateOpen, setCelebrateOpen] = React.useState(false);
  const [celebrateMsg, setCelebrateMsg] = React.useState("");

  // compute average compliance for chip
  let avgCompliance = adherenceData.length
    ? Math.round(adherenceData.reduce((a,b) => a+b, 0) / adherenceData.length)
    : null;

  // Gamification helpers: compute current streak (consecutive days with adherence > 0), level and next reward
  const computeCurrentStreak = (series) => {
    if (!Array.isArray(series) || series.length === 0) return 0;
    let streak = 0;
    for (let i = series.length - 1; i >= 0; i--) {
      if (series[i] > 0) streak++; else break;
    }
    return streak;
  };

  const currentStreak = computeCurrentStreak(adherenceData);
  // level based on avgCompliance: <60 bronze, 60-79 silver, 80-89 gold, >=90 platinum
  const computeLevel = (pct) => {
    if (pct >= 90) return { name: 'Platinum', color: '#e0e0e0' };
    if (pct >= 80) return { name: 'Gold', color: '#ffd54f' };
    if (pct >= 60) return { name: 'Silver', color: '#c0c0c0' };
    return { name: 'Bronze', color: '#cd7f32' };
  };

  const level = computeLevel(avgCompliance);
  // progress towards next level (simple linear from current pct to next threshold)
  const nextThreshold = (avgCompliance >= 90) ? 100 : (avgCompliance >= 80 ? 90 : (avgCompliance >= 60 ? 80 : 60));
  const progressToNext = Math.max(0, Math.min(100, Math.round(((avgCompliance) / nextThreshold) * 100)));

  // receive phase/session data from parent via props
  // accept `phaseProgress`, `phaseTitle`, `completedSessionsList` via restProps
  const {
    phaseProgress: propPhaseProgress,
    phaseProgressDisplay: propPhaseProgressDisplay,
    phaseTitle: propPhaseTitle,
    completedSessionsList: propCompletedSessionsList,
    phase_plans: propPhasePlans,
    planned_sessions_per_day_overall: propPlannedOverall,
    onScrollToJourney,
  } = rest;

  const [sessionWeekOffset, setSessionWeekOffset] = React.useState(0);

  const recentSessionsWeek = React.useMemo(() => {
    const sessions = Array.isArray(propCompletedSessionsList) ? propCompletedSessionsList : [];
    // Use a LOCAL-time YYYY-MM-DD key. `toISOString().slice(0,10)` returns the
    // UTC date — in any non-UTC timezone that can be off by one day, which is
    // why a session completed on Feb 8 was highlighting Feb 9.
    const localDayKey = (d) => {
      const yr = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      return `${yr}-${mo}-${da}`;
    };
    const completedCountByDay = {};
    sessions.forEach((s) => {
      if (!s || !s.completed_date) return;
      const dt = new Date(s.completed_date);
      if (Number.isNaN(dt.getTime())) return;
      const key = localDayKey(dt);
      completedCountByDay[key] = (completedCountByDay[key] || 0) + 1;
    });
    const sortedDays = Object.keys(completedCountByDay).sort();
    const firstCompletedDate = sortedDays.length ? new Date(`${sortedDays[0]}T00:00:00`) : null;
    const lastCompletedDate = sortedDays.length ? new Date(`${sortedDays[sortedDays.length - 1]}T00:00:00`) : null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - sessionWeekOffset * 7);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);

    const days = new Array(7).fill(null).map((_, idx) => {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + idx);
      const key = localDayKey(day);
      const count = completedCountByDay[key] || 0;
      return { date: day.toISOString(), completed: count > 0, count };
    });

    const normFirst = firstCompletedDate && !Number.isNaN(firstCompletedDate.getTime())
      ? new Date(new Date(firstCompletedDate).setHours(0, 0, 0, 0))
      : null;

    return {
      days,
      startDate,
      endDate,
      lastCompletedDate,
      canGoBack: normFirst ? normFirst < startDate : false,
      canGoForward: sessionWeekOffset > 0,
    };
  }, [propCompletedSessionsList, sessionWeekOffset]);

  // use API-driven progress when available, otherwise fall back to locally computed avgCompliance
  const phaseProgressDisplay = propPhaseProgressDisplay || null;
  const displayCompliance = phaseProgressDisplay && phaseProgressDisplay.percent !== undefined
    ? Math.round(phaseProgressDisplay.percent)
    : (propPhaseProgress !== null && typeof propPhaseProgress !== 'undefined') ? Math.round(propPhaseProgress) : avgCompliance;

  // Phase label and percent for Overview header (default to Phase 2: Retrain function)
  const phaseLabel = (phaseProgressDisplay && phaseProgressDisplay.phase_name)
    || propPhaseTitle
    || (useDemoData ? 'Phase 2: Retrain function' : '');
  const phasePercent = phaseProgressDisplay && phaseProgressDisplay.percent !== undefined
    ? Math.round(phaseProgressDisplay.percent)
    : (propPhaseProgress !== null && typeof propPhaseProgress !== 'undefined') ? Math.round(propPhaseProgress) : displayCompliance;
  const formatPhaseAnchorSummary = (summary) => {
    if (summary === null || summary === undefined) {
      return null;
    }

    if (typeof summary === 'string' || typeof summary === 'number') {
      return String(summary);
    }

    if (typeof summary === 'object') {
      return summary.anchor_summary
        || summary.phase_name
        || summary.anchor_label
        || (summary.percent !== undefined && summary.percent !== null ? `${summary.percent}%` : null)
        || summary.anchor_summary_text
        || null;
    }

    return String(summary);
  };

  const phaseAnchorSummary = phaseProgressDisplay && phaseProgressDisplay.anchor_summary
    ? formatPhaseAnchorSummary(phaseProgressDisplay.anchor_summary)
    : null;
  const phaseProgressTooltip = phaseProgressDisplay ? (
    <div style={{ lineHeight: 1.35 }}>
      <div><strong>{phaseLabel}</strong></div>
      {phaseAnchorSummary ? <div>{phaseAnchorSummary}</div> : null}
      {Array.isArray(phaseProgressDisplay.conditions) && phaseProgressDisplay.conditions.length ? (
        phaseProgressDisplay.conditions.map((condition, index) => (
          <div key={`${condition.metric_key || 'condition'}-${index}`}>
            {`${condition.label || condition.metric_key}: ${condition.summary || `${condition.actual_display || '-'} / ${condition.threshold_display || '-'}`} ${condition.passed ? 'passed' : 'pending'}`}
          </div>
        ))
      ) : null}
      {Array.isArray(phaseProgressDisplay.blocking_reasons) && phaseProgressDisplay.blocking_reasons.length ? (
        <div style={{ marginTop: 6 }}><strong>{`Waiting on: ${phaseProgressDisplay.blocking_reasons.join('; ')}`}</strong></div>
      ) : null}
    </div>
  ) : `Progress: ${phasePercent || 0}%`;

  // Activity & pain summary calculations
  const lastN = (arr, n) => arr.slice(Math.max(0, arr.length - n));
  // Use the weekOffset to determine which 7-day slice to show for the weekly activity
  const last7Adherence = getWeekSlice(adherenceData, weekOffset) || [];
  const weekDayAbbrevs = getWeekDayAbbrevs(weekOffset);
  let daysActiveLast7 = (last7Adherence.length ? last7Adherence.filter(v => v > 0).length : 0);
  let avgAdherenceLast7 = Math.round((last7Adherence.reduce((a, b) => a + b, 0) / (last7Adherence.length || 1)));
  const overallDaysActive = adherenceData.filter(v => v > 0).length;
  const avgDailySinceStart = avgCompliance === null ? null : Math.round(avgCompliance);

  // average pain per exercise approximation: use painDailyData as reported pain per session/exercise
  const last7Pain = lastN(painDailyData, 7);
  const avgPainLast7 = (last7Pain.reduce((a, b) => a + b, 0) / (last7Pain.length || 1)).toFixed(1);
  const avgPainOverall = (painDailyData.reduce((a, b) => a + b, 0) / (painDailyData.length || 1)).toFixed(1);

  // compute latest PSFS (function) and pain (inverted so higher is better)
  // latestFunction will be derived later from weekly series (may come from API improvements)
  const latestPain = painDailyData.length ? painDailyData[painDailyData.length - 1] : null;
  const latestFrequencyPercent = painFrequencyData.length ? painFrequencyData[painFrequencyData.length - 1] : null;
  const latestFrequencyDays = latestFrequencyPercent === null
    ? null
    : Math.round((latestFrequencyPercent / 100) * 31);
  const painScore = latestPain === null ? null : Math.max(0, Math.min(100, 100 - latestPain));

  // If improvements data from server is provided, prefer those computed values
  let apiAtBestComp = null;
  let apiAtWorstComp = null;
  let apiFrequencyComp = null;
  // function-specific comparison (PSFS / selfRatedFunction)
  let apiFunctionComp = null;
  // NPS comparison — mirrors DV's extractComparisons() lookup so the patient
  // card sources baseline + follow-up from the same authoritative endpoint.
  let apiNpsComp = null;
  if (improvements && improvements.comparisons && Array.isArray(improvements.comparisons)) {
    const compMap = {};
    improvements.comparisons.forEach(c => { compMap[c.question_identifier] = c; });
    const findComp = (patterns) => {
      for (const k of Object.keys(compMap)) {
        const kl = k.toLowerCase();
        if (patterns.every(p => kl.includes(p))) return compMap[k];
      }
      return null;
    };
    apiAtBestComp = findComp(['atbest']) || findComp(['best']);
    apiAtWorstComp = findComp(['atworst']) || findComp(['worst']);
    apiFrequencyComp = findComp(['frequency']) || findComp(['frequency_of_symptoms']) || findComp(['freq']);
    // look for function-related comparisons (PSFS / selfRatedFunction)
    apiFunctionComp = findComp(['selfratedfunction']) || findComp(['psfs']) || findComp(['function']);
    apiNpsComp = findComp(['nps']) || findComp(['recommend']);
  }

  // DV-parity baseline resolvers: prefer comparison.baseline_value when present,
  // fall back to the legacy series-first-element used elsewhere on this page so
  // the "baseline X / no follow-up yet" UI for fresh treatments is unchanged.
  const numFromComp = (c, k) => (c && c[k] !== null && c[k] !== undefined) ? Number(c[k]) : null;

  // baseline_text for display: prefer improvements.baseline_text, otherwise use baselineSelfFunction first item's text_value
  const baseline_text = (
    (apiFunctionComp && (apiFunctionComp.baseline_text || apiFunctionComp.followup_text)) ||
    (baselineSelfFunction && Array.isArray(baselineSelfFunction.items) && baselineSelfFunction.items.length
      ? (baselineSelfFunction.items[0].text_value || baselineSelfFunction.items[0].answerId || null)
      : null)
  );

  // Display values (prefer API when present)
  const displayPainBestValue = apiAtBestComp && apiAtBestComp.followup_value !== null && apiAtBestComp.followup_value !== undefined ? apiAtBestComp.followup_value : latestPain;
  const displayPainBestOverall = apiAtBestComp && apiAtBestComp.percent_change !== null && apiAtBestComp.percent_change !== undefined ? (-apiAtBestComp.percent_change) : null;
  const displayPainBestLast7 = apiAtBestComp && apiAtBestComp.last7_percent_change !== null && apiAtBestComp.last7_percent_change !== undefined ? (-apiAtBestComp.last7_percent_change) : null;

  const displayPainWorstValue = apiAtWorstComp && apiAtWorstComp.followup_value !== null && apiAtWorstComp.followup_value !== undefined ? apiAtWorstComp.followup_value : (painWorstData && painWorstData.length ? painWorstData[painWorstData.length - 1] : null);
  const displayPainWorstOverall = apiAtWorstComp && apiAtWorstComp.percent_change !== null && apiAtWorstComp.percent_change !== undefined ? (-apiAtWorstComp.percent_change) : null;
  const displayPainWorstLast7 = apiAtWorstComp && apiAtWorstComp.last7_percent_change !== null && apiAtWorstComp.last7_percent_change !== undefined ? (-apiAtWorstComp.last7_percent_change) : null;

  let displayFrequencyDays = latestFrequencyDays;
  let displayFrequencyOverall = null;
  let displayFrequencyLast7 = null;
  if (apiFrequencyComp) {
    const fv = apiFrequencyComp.followup_value;
    // if small number assume days; otherwise treat as percent and convert
    if (typeof fv === 'number') {
      // Interpret small numbers <=7 as days-per-week and convert to monthly days
      // (monthly_days = weekly_days * (31/7)). If value is between 8 and 31 assume it's already monthly days.
      if (fv <= 7) {
        const monthly = (fv * (31.0 / 7.0));
        // keep one decimal for clarity, but allow integer when whole
        displayFrequencyDays = Math.round(monthly * 10) / 10;
      } else if (fv <= 31) {
        displayFrequencyDays = fv;
      } else {
        // treat as percent (0-100)
        displayFrequencyDays = Math.round((fv / 100) * 31);
      }
    }
    displayFrequencyOverall = apiFrequencyComp.percent_change !== null && apiFrequencyComp.percent_change !== undefined ? (-apiFrequencyComp.percent_change) : null;
    displayFrequencyLast7 = apiFrequencyComp.last7_percent_change !== null && apiFrequencyComp.last7_percent_change !== undefined ? (-apiFrequencyComp.last7_percent_change) : null;
  }

  // Build the weekly chart `data` object after we've determined whether API comparisons exist.
  // If API-provided improvements exist, plot baseline and followup points on the weekly series
  // by placing baseline at the oldest displayed week and followup at the newest.
  const weeks = WEEKS_TO_SHOW;
  const freqToPercent = (v) => {
    if (v === null || v === undefined) return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    if (n <= 7) return (n / 7.0) * 100.0; // days/week -> percent of 7-day week
    if (n <= 31) return (n / 31.0) * 100.0; // monthly days -> percent of 31-day month
    return n; // already percent
  };

  // Helper: map a JS Date into the week index (0..weeks-1) for our displayed weekLabels
  const weekIndexForDate = (d) => {
    if (!d) return null;
    const date = (d instanceof Date) ? d : new Date(d);
    for (let i = 0; i < weeks; i++) {
      // compute start and end for week i (oldest->newest)
      const end = new Date();
      end.setDate(end.getDate() - ((weeks - 1 - i) * 7) - (weekOffset * 7));
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      // normalize times to midnight for inclusive comparison
      start.setHours(0,0,0,0);
      end.setHours(23,59,59,999);
      if (date >= start && date <= end) return i;
    }
    return null;
  };

  // Build per-week buckets, allow multiple values per week and average them
  const buildSeriesFromComp = (comp, mapper) => {
    const buckets = new Array(weeks).fill(null).map(() => []);
    if (!comp) return new Array(weeks).fill(null);
    // baseline and followup dates are available under top-level improvements object
    const baseDateRaw = improvements && improvements.baseline && improvements.baseline.date ? improvements.baseline.date : null;
    const followDateRaw = improvements && improvements.followup && improvements.followup.date ? improvements.followup.date : null;
    const baseIdx = weekIndexForDate(baseDateRaw);
    const followIdx = weekIndexForDate(followDateRaw);
    const pushToBucket = (idx, rawVal) => {
      if (idx === null || idx < 0 || idx >= weeks) return;
      const val = (rawVal === null || rawVal === undefined) ? null : mapper(rawVal);
      if (val === null || val === undefined || !Number.isFinite(Number(val))) return;
      buckets[idx].push(Number(val));
    };

    // place baseline and followup into buckets; if date falls outside range, fallback to ends
    if (comp.baseline_value !== null && comp.baseline_value !== undefined) {
      const idx = (baseIdx !== null) ? baseIdx : 0;
      pushToBucket(idx, comp.baseline_value);
    }
    if (comp.followup_value !== null && comp.followup_value !== undefined) {
      const idx = (followIdx !== null) ? followIdx : (weeks - 1);
      pushToBucket(idx, comp.followup_value);
    }

    // compute average per bucket
    const series = buckets.map(arr => {
      if (!arr || !arr.length) return null;
      const s = arr.reduce((a,b) => a+b, 0) / arr.length;
      return Number.isFinite(s) ? Number(parseFloat(s.toFixed(2))) : null;
    });

    // interpolate linear gaps so the chart draws a connected line
    const interpolate = (arr) => {
      const out = arr.slice();
      // find first non-null
      let first = -1;
      for (let i = 0; i < out.length; i++) if (out[i] !== null) { first = i; break; }
      if (first === -1) return out; // all null
      // fill leading nulls with first
      for (let i = 0; i < first; i++) out[i] = out[first];
      // interpolate between known points
      let i = first;
      while (i < out.length) {
        if (out[i] === null) { i++; continue; }
        let j = i + 1;
        while (j < out.length && out[j] === null) j++;
        if (j >= out.length) {
          // fill trailing nulls with last known
          for (let k = i+1; k < out.length; k++) out[k] = out[i];
          break;
        }
        // linear interpolate between i and j
        const vi = out[i];
        const vj = out[j];
        const steps = j - i;
        for (let k = 1; k < steps; k++) {
          const t = k / steps;
          out[i + k] = Number(parseFloat((vi + (vj - vi) * t).toFixed(2)));
        }
        i = j;
      }
      return out;
    };

    return interpolate(series);
  };

  const buildSeriesFromHistory = (historyItems, mapper) => {
    const buckets = new Array(weeks).fill(null).map(() => []);
    if (!Array.isArray(historyItems) || !historyItems.length) return new Array(weeks).fill(null);

    historyItems.forEach((item, index) => {
      const idx = weekIndexForDate(item && item.date ? item.date : null);
      const fallbackIdx = idx !== null ? idx : Math.min(weeks - 1, Math.max(0, index));
      if (fallbackIdx === null || fallbackIdx < 0 || fallbackIdx >= weeks) return;
      const rawVal = item && item.value;
      const val = (rawVal === null || rawVal === undefined) ? null : mapper(rawVal);
      if (val === null || val === undefined || !Number.isFinite(Number(val))) return;
      buckets[fallbackIdx].push(Number(val));
    });

    const series = buckets.map((arr) => {
      if (!arr || !arr.length) return null;
      const average = arr.reduce((sum, value) => sum + value, 0) / arr.length;
      return Number.isFinite(average) ? Number(parseFloat(average.toFixed(2))) : null;
    });

    const filled = series.slice();
    let firstKnown = -1;
    for (let index = 0; index < filled.length; index += 1) {
      if (filled[index] !== null) {
        firstKnown = index;
        break;
      }
    }
    if (firstKnown === -1) return filled;
    for (let index = 0; index < firstKnown; index += 1) {
      filled[index] = filled[firstKnown];
    }
    let left = firstKnown;
    while (left < filled.length) {
      if (filled[left] === null) {
        left += 1;
        continue;
      }
      let right = left + 1;
      while (right < filled.length && filled[right] === null) {
        right += 1;
      }
      if (right >= filled.length) {
        for (let index = left + 1; index < filled.length; index += 1) {
          filled[index] = filled[left];
        }
        break;
      }
      const leftValue = filled[left];
      const rightValue = filled[right];
      const stepCount = right - left;
      for (let index = 1; index < stepCount; index += 1) {
        const ratio = index / stepCount;
        filled[left + index] = Number(parseFloat((leftValue + (rightValue - leftValue) * ratio).toFixed(2)));
      }
      left = right;
    }
    return filled;
  };

  const buildWeeklyComplianceSeries = (sessions, plannedPerDay, totalWeeks = WEEKS_TO_SHOW) => {
    const labels = new Array(totalWeeks).fill(null).map((_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - ((totalWeeks - 1 - index) * 7));
      return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    });

    if (!Array.isArray(sessions) || !sessions.length || !plannedPerDay) {
      return { labels, values: [] };
    }

    const localDayKey = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const countsByDay = {};
    sessions.forEach((session) => {
      const completedAt = session && session.completed_date ? new Date(session.completed_date) : null;
      if (!completedAt || Number.isNaN(completedAt.getTime())) return;
      const key = localDayKey(completedAt);
      countsByDay[key] = (countsByDay[key] || 0) + 1;
    });

    const weeklyPlan = plannedPerDay * 7;
    const values = labels.map((_, index) => {
      const weekEnd = new Date();
      weekEnd.setHours(0, 0, 0, 0);
      weekEnd.setDate(weekEnd.getDate() - ((totalWeeks - 1 - index) * 7));
      let completedCount = 0;
      for (let offset = 0; offset < 7; offset += 1) {
        const day = new Date(weekEnd);
        day.setDate(weekEnd.getDate() - (6 - offset));
        const key = localDayKey(day);
        completedCount += countsByDay[key] || 0;
      }
      return Number(Math.max(0, Math.min(100, (completedCount / weeklyPlan) * 100)).toFixed(1));
    });

    return { labels, values };
  };

  if (apiAtBestComp) {
    weeklyPainDaily = buildSeriesFromComp(apiAtBestComp, (v) => Number(v));
  }
  if (apiAtWorstComp) {
    weeklyPainWorst = buildSeriesFromComp(apiAtWorstComp, (v) => Number(v));
  }
  if (apiFrequencyComp) {
    weeklyPainFrequency = buildSeriesFromComp(apiFrequencyComp, (v) => freqToPercent(v));
  }

  // If we have an API-provided function comparison, build the weekly function series
  if (apiFunctionComp) {
    try {
      weeklyFunction = buildSeriesFromComp(apiFunctionComp, (v) => Number(v));
    } catch (e) {
      // fall back silently to existing aggregated functionData
      console.error('Failed to build weeklyFunction from apiFunctionComp', e);
    }
  } else if (baselineSelfFunction && Array.isArray(baselineSelfFunction.items) && baselineSelfFunction.items.length) {
    // fallback: use baselineSelfFunction numeric average as a flat series (will be interpolated later)
    try {
      const nums = baselineSelfFunction.items.map(i => {
        const v = (i && i.numeric_value);
        return (typeof v === 'number') ? v : (v ? Number(v) : NaN);
      }).filter(n => !Number.isNaN(n));
      if (nums.length) {
        const avg = nums.reduce((s, v) => s + v, 0) / nums.length;
        weeklyFunction = new Array(weeks).fill(Number(parseFloat(avg.toFixed(2))));
      }
    } catch (e) {
      // ignore
    }
  }

  const KPI_CHART_COLORS = ['#9c27b0', '#00bcd4', '#8bc34a', '#ff5722', '#607d8b', '#e91e63', '#795548', '#03a9f4'];

  // Resolve NPS kpi (for both UI card and chart dataset). DV-parity sourcing:
  //   1. improvements.comparisons (cross-version diff)  — same as getMetricSnapshot
  //   2. KpiMeasurement row marked is_active_baseline    — baseline
  //   3. KpiMeasurement.numerical_value                  — latest patient-rated value
  //   4. history first/last value                        — final fallback
  // The legacy `npsData` demo array is only used when no clinician KPI exists.
  const npsKpi = Array.isArray(clinicianKpis)
    ? clinicianKpis.find((kpi) => kpi && !kpi.is_deleted && !kpi.paused_from_followup && kpi.kpi_type === 'nps')
    : null;
  const npsHistoryRaw = npsKpi && Array.isArray(npsKpi.history) ? npsKpi.history : [];
  const npsHistorySeries = npsKpi
    ? npsHistoryRaw
        .map((historyItem, index) => {
          const rawValue = historyItem && historyItem.value !== null && historyItem.value !== undefined
            ? Number(historyItem.value)
            : null;
          if (rawValue === null || !Number.isFinite(rawValue)) return null;
          const label = historyItem && historyItem.date
            ? new Date(historyItem.date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })
            : `v${(historyItem && historyItem.version) || index + 1}`;
          return {
            value: rawValue,
            date: historyItem && historyItem.date ? historyItem.date : null,
            label,
          };
        })
        .filter(Boolean)
    : [];
  const npsHistValues = npsHistorySeries.length
    ? npsHistorySeries.map((item) => item.value)
    : npsData;
  const npsActiveBaselinePoint = npsHistoryRaw.find((h) => h && h.is_active_baseline)
    || npsHistoryRaw.find((h) => h && h.value !== null && h.value !== undefined)
    || (npsHistoryRaw.length ? npsHistoryRaw[0] : null);
  const npsBaselineFromHistory = npsActiveBaselinePoint && npsActiveBaselinePoint.value !== null && npsActiveBaselinePoint.value !== undefined
    ? Number(npsActiveBaselinePoint.value)
    : (npsHistValues.length ? npsHistValues[0] : null);
  const npsLatestFromKpi = npsKpi && npsKpi.numerical_value !== null && npsKpi.numerical_value !== undefined
    ? Number(npsKpi.numerical_value)
    : (npsHistValues.length ? npsHistValues[npsHistValues.length - 1] : null);
  const npsBaselineVal = numFromComp(apiNpsComp, 'baseline_value') !== null
    ? numFromComp(apiNpsComp, 'baseline_value')
    : npsBaselineFromHistory;
  const latestNps = numFromComp(apiNpsComp, 'followup_value') !== null
    ? numFromComp(apiNpsComp, 'followup_value')
    : npsLatestFromKpi;
  let weeklyNps = aggregateToWeeks(npsHistValues);
  if (npsHistorySeries.length) {
    weeklyNps = buildSeriesFromHistory(npsHistorySeries, (value) => Number(value));
  } else if (apiNpsComp) {
    weeklyNps = buildSeriesFromComp(apiNpsComp, (value) => Number(value));
  }

  const kpiChartDatasets = (() => {
    const STANDARD_PAIN_SUBTYPES = new Set(['best', 'worst', 'current', 'frequency', 'rest', 'activity', 'difficulty']);
    const extraKpis = Array.isArray(clinicianKpis)
      ? clinicianKpis
          .filter((kpi) => {
            if (!kpi || kpi.is_deleted || kpi.paused_from_followup) return false;
            if (kpi.kpi_type === 'function_goal' || kpi.kpi_type === 'nps') return false;
            const subtype = String(kpi.kpi_subtype || '').toLowerCase();
            if (kpi.kpi_type === 'pain_baseline' && STANDARD_PAIN_SUBTYPES.has(subtype)) return false;
            const qid = String(kpi.question_identifier || '').toLowerCase();
            if (qid.includes('nps_selfrated') || qid.includes('selfrated_nps') || qid.includes('selfratednps') || qid.includes('netpromoter') || qid === 'nps' || qid.startsWith('nps_')) return false;
            return true;
          })
          .map((kpi, idx) => {
            const color = KPI_CHART_COLORS[idx % KPI_CHART_COLORS.length];
            const histValues = (Array.isArray(kpi.history) ? kpi.history : [])
              .map((h) => (h && h.value !== null && h.value !== undefined) ? Number(h.value) : null)
              .filter((v) => v !== null);
            return {
              label: kpi.label || kpi.kpi_subtype || String(kpi.id || 'KPI'),
              data: aggregateToWeeks(histValues),
              fill: false,
              backgroundColor: 'transparent',
              borderColor: color,
              pointBackgroundColor: color,
              tension: 0.4,
            };
          })
      : [];
    // NPS dataset
    const npsDs = {
      label: 'NPS',
      data: weeklyNps,
      fill: false,
      backgroundColor: 'transparent',
      borderColor: '#2f6fed',
      pointBackgroundColor: '#2f6fed',
      tension: 0.4,
      ringMaxValue: 10,
    };
    // Compliance dataset
    const complianceDs = {
      label: 'Compliance with program',
      data: aggregateToWeeks(adherenceData),
      fill: false,
      backgroundColor: 'transparent',
      borderColor: '#2f6fed',
      pointBackgroundColor: '#2f6fed',
      tension: 0.4,
      ringMaxValue: 100,
    };
    return [npsDs, complianceDs, ...extraKpis];
  })();

  const data = {
    labels: weekLabels,
    datasets: [
      { ...baseData.datasets[0], data: weeklyFunction },
      { ...baseData.datasets[1], data: weeklyPainDaily },
      { ...baseData.datasets[2], data: weeklyPainWorst },
      { ...baseData.datasets[3], data: weeklyPainFrequency },
      ...kpiChartDatasets,
    ]
  };

  // derive latest function value from the weeklyFunction series (fallback to raw functionData)
  const latestFunction = (Array.isArray(weeklyFunction) && weeklyFunction.length) ? weeklyFunction[weeklyFunction.length - 1] : (Array.isArray(functionData) && functionData.length ? functionData[functionData.length - 1] : null);

  // Now that weeklyFunction has been computed (either from improvements or baseline fallback),
  // resolve the series we will use for function-related calculations. Prefer weeklyFunction
  // when available; otherwise fall back to the static functionData.
  try {
    _funcSeriesForCalc = (Array.isArray(weeklyFunction) && weeklyFunction.length) ? weeklyFunction : functionData;
    if (Array.isArray(_funcSeriesForCalc) && _funcSeriesForCalc.length) {
      functionImprovement = (_funcSeriesForCalc[_funcSeriesForCalc.length - 1] - _funcSeriesForCalc[0]);
      functionPercent = ((_funcSeriesForCalc[0] ? (functionImprovement / _funcSeriesForCalc[0]) : 0) * 100).toFixed(1);
      funcChanges = computePercentChange(_funcSeriesForCalc, WINDOW_DAYS);
      funcBaseline = _funcSeriesForCalc[0];
    } else {
      // leave defaults (zeros) if no data available
      _funcSeriesForCalc = [];
      functionImprovement = 0;
      functionPercent = '0.0';
      funcChanges = { overall: 0, window: 0 };
      funcBaseline = 0;
    }
  } catch (e) {
    // defensive: ensure variables have sensible defaults on any unexpected error
    _funcSeriesForCalc = [];
    functionImprovement = 0;
    functionPercent = '0.0';
    funcChanges = { overall: 0, window: 0 };
    funcBaseline = 0;
    console.error('Error resolving function series for calculations', e);
  }

  // visible state for datasets so React can show visual changes immediately
  const [visibleDatasets, setVisibleDatasets] = React.useState(() => data.datasets.map(() => true));

  // When datasets are added (e.g. clinicianKpis loads after mount), extend the
  // visibility array so every new dataset starts as visible rather than undefined.
  React.useEffect(() => {
    setVisibleDatasets(prev => {
      if (prev.length >= data.datasets.length) return prev;
      return [...prev, ...new Array(data.datasets.length - prev.length).fill(true)];
    });
  }, [data.datasets.length]);

  // When the selected week changes or the improvements payload changes, ensure the
  // Chart.js instance re-renders so newly computed `data` is reflected visually.
  React.useEffect(() => {
    const c = getChart();
    if (!c) return;
    try {
      if (typeof c.update === 'function') c.update();
    } catch (e) {
      // ignore chart update failures silently
    }
  }, [weekOffset, improvements]);

  // small helper to render a circular ring used in the custom legend
  const renderRing = (color, value, size = 48, labelInside, emptyColor = 'rgba(89, 93, 89, 0.18)', maxValue = 100) => {
    const numeric = (value === null || value === undefined) ? NaN : Number(value);
    const valid = Number.isFinite(numeric);
    const pct = valid ? Math.max(0, Math.min(1, numeric / maxValue)) : 0;
    const deg = pct * 360;
    // reversed gradient: empty portion first, then colored arc at the end so the fill appears reversed
    const gradient = `conic-gradient(${emptyColor} 0deg ${360 - deg}deg, ${color} ${360 - deg}deg 360deg)`;
    return (
      <Box style={{
        width: size,
        height: size,
        borderRadius: '50%',
        padding: 6,
        background: 'rgba(255,255,255,0.95)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: gradient
        }}>
          <Box style={{
            width: '69%',
            height: '69%',
            borderRadius: '50%',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            color: '#002D55',
            fontSize: 'clamp(10px, 0.94vw, 14px)',
          }}>
            {(labelInside !== undefined && labelInside !== null) ? labelInside : (valid ? `${Math.round(numeric)}` : '--')}
          </Box>
        </Box>
      </Box>
    );
  };

  const handleSubGoalChange = (idx, val) => {
    const newVals = [...subGoalValues];
    newVals[idx] = val;
    setSubGoalValues(newVals);
    localStorage.setItem('subGoalValues', JSON.stringify(newVals));
    if (val === 100) {
      setCelebrateMsg(`Sub-goal achieved: ${subGoals[idx]}! 🎉`);
      setCelebrateOpen(true);
    }
  };

  const handleSymptomChange = (idx, val) => {
    const newVals = [...symptomValues];
    newVals[idx] = val;
    setSymptomValues(newVals);
    localStorage.setItem('symptomValues', JSON.stringify(newVals));
    if (val === 100) {
      setCelebrateMsg(`Symptom improvement: ${symptoms[idx]}! 🎉`);
      setCelebrateOpen(true);
    }
  };

  const handleAddSubGoal = () => {
    if (newSubGoal.trim()) {
      setSubGoals([...subGoals, newSubGoal.trim()]);
      setSubGoalValues([...subGoalValues, 0]);
      setNewSubGoal("");
    }
  };

  const handleAddSymptom = () => {
    if (newSymptom.trim()) {
      setSymptoms([...symptoms, newSymptom.trim()]);
      setSymptomValues([...symptomValues, 0]);
      setNewSymptom("");
    }
  };

  React.useEffect(() => {
    const savedSubGoals = localStorage.getItem('subGoalValues');
    const savedSymptoms = localStorage.getItem('symptomValues');
    if (savedSubGoals) setSubGoalValues(JSON.parse(savedSubGoals));
    if (savedSymptoms) setSymptomValues(JSON.parse(savedSymptoms));
  }, []);

  // --- Summary calculations (overall and last-7d effect sizes & compliance) ---
  // Prefer backend-provided planned sessions/day; fall back to a conservative default
  const plannedSessionsPerDay = (typeof propPlannedOverall === 'number' && !Number.isNaN(propPlannedOverall) && propPlannedOverall > 0) ? Number(propPlannedOverall) : 3;

  // Compute compliance from API-provided completed sessions when available
  let overallCompliancePercent = avgDailySinceStart; // fallback
  let last7CompliancePercent = avgCompliance === null ? null : avgAdherenceLast7; // fallback
  let last7SessionsCompleted = last7CompliancePercent === null
    ? null
    : Math.round((last7CompliancePercent / 100) * plannedSessionsPerDay);
  let overallSessionsAvg = overallCompliancePercent === null
    ? null
    : Math.round((overallCompliancePercent / 100) * plannedSessionsPerDay);

  if (Array.isArray(propCompletedSessionsList) && propCompletedSessionsList.length > 0) {
    // build counts per day (YYYY-MM-DD)
    const counts = {};
    let minDate = null;
    propCompletedSessionsList.forEach(s => {
      const d = s && s.completed_date ? new Date(s.completed_date) : null;
      if (!d || Number.isNaN(d.getTime())) return;
      const key = d.toISOString().slice(0,10);
      counts[key] = (counts[key] || 0) + 1;
      if (!minDate || d < minDate) minDate = d;
    });

    const today = new Date();
    // determine the tracking window (days since first recorded session)
    const totalDays = minDate ? (Math.floor((today.setHours(0,0,0,0) - new Date(minDate).setHours(0,0,0,0)) / 86400000) + 1) : 1;
    const totalCompleted = Object.values(counts).reduce((a,b) => a + b, 0);
    const totalPlanned = Math.max(1, totalDays * plannedSessionsPerDay);
    overallCompliancePercent = Math.round(Math.min(100, (totalCompleted / totalPlanned) * 100));

    // last 7 days window
    let last7Completed = 0;
    let activeDays7 = 0;
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate((new Date()).getDate() - (6 - i));
      const key = d.toISOString().slice(0,10);
      const c = counts[key] || 0;
      last7Completed += c;
      if (c > 0) activeDays7++;
    }
    const plannedLast7 = Math.max(1, plannedSessionsPerDay * 7);
    last7CompliancePercent = Math.round(Math.min(100, (last7Completed / plannedLast7) * 100));
    last7SessionsCompleted = Math.round((last7CompliancePercent / 100) * plannedSessionsPerDay);
    overallSessionsAvg = Math.round((overallCompliancePercent / 100) * plannedSessionsPerDay);

    // expose computed values into variables used by the UI
    avgCompliance = overallCompliancePercent;
    avgAdherenceLast7 = last7CompliancePercent;
    daysActiveLast7 = activeDays7;
  }

  // effect sizes: overall (since start) and last 7 days (window)
  const funcEffectOverall = funcChanges.overall; // computePercentChange already computed
  const funcEffectLast7 = computePercentChange(_funcSeriesForCalc, 7).window;
  // 7-item window average for function (used in "from X to Y" phrasing)
  const funcWindowSlice = (() => {
    const arr = Array.isArray(_funcSeriesForCalc) ? _funcSeriesForCalc : [];
    const w = Math.min(7, arr.length);
    const start = Math.max(0, arr.length - w);
    const slice = arr.slice(start);
    const avg = slice.length ? (slice.reduce((s, v) => s + v, 0) / slice.length) : 0;
    return avg;
  })();

  // Baselines and 7-day averages for pain and frequency (for "from X to Y" phrasing)
  const painBestBaseline = (Array.isArray(painDailyData) && painDailyData.length) ? painDailyData[0] : null;
  const painBestWindowAvg = (() => {
    const w = Math.min(7, painDailyData.length);
    const start = Math.max(0, painDailyData.length - w);
    const slice = painDailyData.slice(start);
    return slice.reduce((s, v) => s + v, 0) / (slice.length || 1);
  })();

  const painWorstBaseline = (Array.isArray(painWorstData) && painWorstData.length) ? painWorstData[0] : null;
  const painWorstWindowAvg = (() => {
    const w = Math.min(7, painWorstData.length);
    const start = Math.max(0, painWorstData.length - w);
    const slice = painWorstData.slice(start);
    return slice.reduce((s, v) => s + v, 0) / (slice.length || 1);
  })();

  const freqBaseline = (Array.isArray(painFrequencyData) && painFrequencyData.length) ? painFrequencyData[0] : null;
  const freqWindowAvg = (() => {
    const w = Math.min(7, painFrequencyData.length);
    const start = Math.max(0, painFrequencyData.length - w);
    const slice = painFrequencyData.slice(start);
    return slice.reduce((s, v) => s + v, 0) / (slice.length || 1);
  })();

  // pain (at best) improvements: computePercentChange returns positive when values increased, but
  // for pain lower is improvement -> invert the sign to show positive improvement when pain reduces
  const painBestEffectOverall = painOverallImprovement; // inverted earlier
  const painBestEffectLast7 = -computePercentChange(painDailyData, 7).window;

  const painWorstEffectOverall = worstOverallImprovement;
  const painWorstEffectLast7 = -computePercentChange(painWorstData, 7).window;

  const freqEffectOverall = freqOverallImprovement;
  const freqEffectLast7 = -computePercentChange(painFrequencyData, 7).window;

  // --- Compliance scoring & patient-facing message variants ---
  const complianceScore = (pct) => {
    const p = Number.isFinite(pct) ? Math.round(pct) : 0;
    if (p >= 90) return {
      level: 'excellent',
      short: "Outstanding — keep it up!",
      message: "Fantastic — you’re hitting nearly all your sessions. This consistency helps progress and reduces flare-ups."
    };
    if (p >= 80) return {
      level: 'good',
      short: "Nice work — you're following your plan most days.",
      message: "Great work — you’ve been consistent this week. Small, steady progress like this adds up over time."
    };
    if (p >= 60) return {
      level: 'okay',
      short: "Good effort — keep building the habit.",
      message: "Good effort — try to increase consistency a bit this week (an extra short session a few days can make a difference)."
    };
    if (p > 0) return {
      level: 'low',
      short: "Keep going — small steps add up.",
      message: "You’ve started — small, regular steps will help. Try adding one short session on a couple more days this week."
    };
    return {
      level: 'none',
      short: "No activity recorded yet.",
      message: "We don’t have any recorded sessions. Try completing one short session today — it’s a good place to start."
    };
  };

  const last7ComplianceMsg = complianceScore(last7CompliancePercent);
  const avgAdherenceMsg = complianceScore(avgAdherenceLast7);

  const goalMetricCards = React.useMemo(() => {
    if (!Array.isArray(clinicianKpis) || !clinicianKpis.length) return [];

    return clinicianKpis
      // Defensive: backend already filters soft-deleted/paused rows, but
      // skip them client-side too in case a stale prop slips through.
      .filter((kpi) => kpi && !kpi.is_deleted && !kpi.paused_from_followup)
      .filter((kpi) => kpi && kpi.kpi_type === 'function_goal')
      .map((kpi, idx) => {
        const label = kpi.label || kpi.kpi_subtype || kpi.id || `Goal ${idx + 1}`;
        const history = Array.isArray(kpi.history) ? kpi.history : [];

        let series = history
          .map((h) => (h && h.value !== null && h.value !== undefined ? Number(h.value) : null))
          .filter((v) => Number.isFinite(v));

        if (!series.length && kpi.numerical_value !== null && kpi.numerical_value !== undefined) {
          const n = Number(kpi.numerical_value);
          if (Number.isFinite(n)) series = [n];
        }

        if (!series.length) return null;

        const baseline = series[0];
        const current = series[series.length - 1];
        const overallPct = baseline === 0 ? 0 : ((current - baseline) / Math.abs(baseline)) * 100;
        const last7Pct = computePercentChange(series, 7).window;

        return {
          key: String(kpi.id || `${label}-${idx}`),
          label,
          baseline,
          current,
          overallPct,
          last7Pct,
        };
      })
      .filter(Boolean);
  }, [clinicianKpis]);

  const nonGoalClinicianKpis = React.useMemo(
    () => {
      if (!Array.isArray(clinicianKpis)) return [];
      // The "Clinician-tracked metrics" panel is for KPIs that aren't already
      // surfaced via the headline pain / function / NPS cards. Standard pain
      // subtypes (best / worst / current / frequency / rest / activity /
      // difficulty), the function goal and the NPS row would otherwise render
      // a duplicate card here with raw labels like "best" or "worst".
      const STANDARD_PAIN_SUBTYPES = new Set(['best', 'worst', 'current', 'frequency', 'rest', 'activity', 'difficulty']);
      return clinicianKpis.filter((kpi) => {
        if (!kpi) return false;
        // Defensive: backend already filters soft-deleted/paused rows out
        // of /kpi_items/, but skip them here too so a stale cache or
        // partial response can never resurrect a removed manual baseline.
        if (kpi.is_deleted) return false;
        if (kpi.paused_from_followup) return false;
        if (kpi.kpi_type === 'function_goal') return false;
        if (kpi.kpi_type === 'nps') return false;
        const subtype = String(kpi.kpi_subtype || '').toLowerCase();
        if (kpi.kpi_type === 'pain_baseline' && STANDARD_PAIN_SUBTYPES.has(subtype)) return false;
        const qid = String(kpi.question_identifier || '').toLowerCase();
        if (qid.includes('nps_selfrated') || qid.includes('selfrated_nps') || qid.includes('selfratednps') || qid.includes('netpromoter') || qid === 'nps' || qid.startsWith('nps_')) return false;
        return true;
      });
    },
    [clinicianKpis]
  );
  

  // ----------------------------------------------------------------
  // DevelopmentView-style data-point card helpers (mirrors the
  // "Patient-reported outcomes" layout in DevelopmentView.js).
  // Inlined here so the patient ChartProgress renders headline metrics,
  // compliance/phase, and clinician-tracked KPIs in the same wide
  // card-with-mini-graph format used on the clinician side, while
  // preserving the existing colour coding for value pills.
  // ----------------------------------------------------------------
  const fmt1 = (v) => {
    if (v === null || v === undefined) return '-';
    const n = Number(v);
    return Number.isFinite(n) ? (Number.isInteger(n) ? String(n) : n.toFixed(1)) : '-';
  };
  const numOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const trendColor = (delta, lowerIsBetter) => {
    if (delta === null || delta === undefined) return '#002D55';
    const d = Number(delta);
    if (d === 0) return '#f0b700';
    const improved = lowerIsBetter ? d < 0 : d > 0;
    return improved ? '#00a152' : '#d84315';
  };
  // Returns {color,bg} pill colours given a value normalised to 0-100
  // and direction (lowerIsBetter true => low values are good).
  const pillStyle = (value, lowerIsBetter) => {
    const n = numOrNull(value);
    if (n === null) return { color: '#6d7993', bg: '#eef2f7' };
    if (lowerIsBetter) {
      if (n <= 30) return { color: '#0d7a2b', bg: '#dcf5e6' };
      if (n <= 50) return { color: '#926b00', bg: '#fff4d1' };
      if (n <= 65) return { color: '#b23a00', bg: '#ffe4d6' };
      return { color: '#9f2020', bg: '#fde0e0' };
    }
    if (n >= 70) return { color: '#0d7a2b', bg: '#dcf5e6' };
    if (n >= 50) return { color: '#926b00', bg: '#fff4d1' };
    if (n >= 35) return { color: '#b23a00', bg: '#ffe4d6' };
    return { color: '#9f2020', bg: '#fde0e0' };
  };

  // Build a metric card (text on the left, mini Line chart on the
  // right). `pillScaleMax` lets non-100 metrics (e.g. frequency in /7d)
  // reuse the same colour thresholds by normalising the displayed value
  // back to a 0-100 percentage for pillStyle's decisions.
  const renderMetricCard = ({
    cardKey,
    label,
    current,
    baseline,
    unit,
    lowerIsBetter,
    seriesData = [],
    seriesLabels = [],
    lineColor = '#fb741a',
    lineBg = 'rgba(251,116,26,0.12)',
    pillScaleMax = 100,
    yMax = null,
    noDataText = 'No graph data yet.',
  }) => {
    const cardCurrent = numOrNull(current);
    const cardBaseline = numOrNull(baseline);
    const cardDelta = (cardCurrent !== null && cardBaseline !== null) ? (cardCurrent - cardBaseline) : null;
    const imp = (cardDelta !== null && cardBaseline !== null && cardBaseline !== 0)
      ? ((lowerIsBetter ? -cardDelta : cardDelta) / Math.abs(cardBaseline)) * 100
      : null;
    const numericSeries = (seriesData || [])
      .filter((v) => v !== null && v !== undefined)
      .map(Number)
      .filter(Number.isFinite);
    const last7dChange = numericSeries.length >= 2
      ? (numericSeries[numericSeries.length - 1] - numericSeries[numericSeries.length - 2])
      : null;
    const last7dImprovement = last7dChange === null ? null : (lowerIsBetter ? -last7dChange : last7dChange);
    const pillNorm = (cardCurrent !== null && pillScaleMax)
      ? (cardCurrent / pillScaleMax) * 100
      : cardCurrent;
    const pill = pillStyle(pillNorm, lowerIsBetter);
    const resolvedYMax = yMax !== null && yMax !== undefined
      ? yMax
      : (unit === '/100' || unit === '%' ? 100 : unit === '/10' ? 10 : unit === '/7d' ? 7 : null);
    return (
      <Paper key={cardKey} elevation={0} style={{ borderRadius: 12, background: 'linear-gradient(180deg,#fff,#f8fbff)', border: '1px solid rgba(0,45,85,0.12)', boxShadow: '0 3px 10px rgba(0,45,85,0.08)', padding: 12, height: '100%' }}>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(12px, 0.9vw, 15px)' }}>{label}</Typography>
              <Box component="span" style={{ padding: '2px 8px', borderRadius: 999, fontWeight: 900, color: pill.color, background: pill.bg, fontSize: 12 }}>
                {cardCurrent !== null ? `${fmt1(cardCurrent)}${unit}` : '-'}
              </Box>
            </div>
            <Typography style={{ fontWeight: 900, fontSize: 'clamp(11px, 2vw, 13px)', color: '#002D55', lineHeight: 1.1 }}>
              {cardCurrent !== null ? `Latest: ${fmt1(cardCurrent)}${unit}` : 'Latest: -'}
            </Typography>
            <Typography style={{ color: '#486d86', fontSize: 'clamp(11px, 0.9vw, 13px)', marginTop: 2 }}>
              {cardBaseline !== null ? `Baseline: ${fmt1(cardBaseline)}${unit}` : 'Baseline: -'}
            </Typography>
            <Typography style={{ color: trendColor(cardDelta, lowerIsBetter), fontWeight: 700, fontSize: 'clamp(11px, 0.9vw, 13px)', marginTop: 2 }}>
              {cardDelta !== null ? `${cardDelta > 0 ? '+' : ''}${fmt1(cardDelta)} ${unit}` : 'No absolute change'}
            </Typography>
            <Typography style={{ color: '#486d86', fontSize: 'clamp(11px, 0.9vw, 13px)', marginTop: 2 }}>
              {imp !== null ? `${fmt1(Math.abs(imp))}% ${imp >= 0 ? 'improvement' : 'worsening'} since start` : 'No improvement data yet'}
            </Typography>
            <Typography style={{ color: '#486d86', fontSize: 'clamp(11px, 0.9vw, 13px)', marginTop: 2 }}>
              Last 7d: {last7dImprovement !== null ? `${fmt1(Math.abs(last7dImprovement))}% ${last7dImprovement >= 0 ? 'improved' : 'worsened'}` : '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box style={{ minHeight: 160, borderRadius: 10, padding: 8, background: '#fff', border: '1px solid #d9d9d9' }}>
              {Array.isArray(seriesData) && seriesData.some((v) => v !== null && v !== undefined) ? (
                <Line
                  data={{
                    labels: seriesLabels,
                    datasets: [{
                      label,
                      data: seriesData,
                      borderColor: lineColor,
                      backgroundColor: lineBg,
                      borderWidth: 2,
                      fill: true,
                      tension: 0.32,
                      pointRadius: 2,
                      spanGaps: true,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false }, tooltip: { enabled: true } },
                    scales: {
                      x: { grid: { display: false }, ticks: { color: '#6d7993', font: { size: 10 } } },
                      y: {
                        grid: { color: 'rgba(0,45,85,0.08)' },
                        ticks: { color: '#6d7993', font: { size: 10 }, beginAtZero: true },
                        min: resolvedYMax !== null ? 0 : undefined,
                        max: resolvedYMax !== null ? resolvedYMax : undefined,
                      },
                    },
                  }}
                />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography style={{ color: '#8f98a6', fontStyle: 'italic', fontSize: 'clamp(11px, 0.9vw, 13px)' }}>{noDataText}</Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // Frequency: convert weeklyPainFrequency (% of week) into days/week
  // for the dataPointCard graph; preserves the requested 0-7 y-axis.
  const freqPctToDaysWeek = (p) => (p === null || p === undefined) ? null : Number(((Number(p) / 100) * 7).toFixed(1));
  const freqDaysWeekSeries = Array.isArray(weeklyPainFrequency) ? weeklyPainFrequency.map(freqPctToDaysWeek) : [];
  const freqCurrentDaysWeek = freqDaysWeekSeries.length ? freqDaysWeekSeries[freqDaysWeekSeries.length - 1] : null;
  const freqBaselineDaysWeek = freqDaysWeekSeries.length ? freqDaysWeekSeries[0] : null;

  // Compliance series (one point per week of the displayed window).
  const complianceWeeklySeries = buildWeeklyComplianceSeries(propCompletedSessionsList, plannedSessionsPerDay, WEEKS_TO_SHOW);

  return (
    <Card className={clsx(classes.root, className)} {...rest}>
      <CardHeader
        avatar={<Avatar className={classes.avatar}><BarChartIcon style={{ color: '#fff', fontSize: 'clamp(14px, 1.4vw, 22px)' }} /></Avatar>}

        title={<span className={classes.cardHeaderTitle}>Overview of Your Journey.</span>}
        className={classes.cardHeaderRoot}
        style={{ marginBottom: 5, padding: 10, borderBottom: '2px solid #fb741a', alignItems: 'center' }}
      />
      
      {/* Improvements summary (baseline vs followup) - rendered when improvements prop is provided */}
       {/*
      {improvements && improvements.comparisons && Array.isArray(improvements.comparisons) && (
        <CardContent style={{ paddingTop: 0 }}>
          <Box style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
            {(improvements.comparisons || []).slice(0, 400).map((c, idx) => {
              const pct = c.percent_change !== null && c.percent_change !== undefined ? Number(c.percent_change).toFixed(1) : null;
              const last7 = c.last7_percent_change !== null && c.last7_percent_change !== undefined ? Number(c.last7_percent_change).toFixed(1) : null;
              const absCh = c.absolute_change !== null && c.absolute_change !== undefined ? c.absolute_change : null;
              const mainColor = pct !== null ? (pct > 0 ? '#00a152' : '#d32f2f') : '#486d86';
              const last7Color = last7 !== null ? (last7 > 0 ? '#00a152' : '#d32f2f') : '#6d7993';
              return (
                <Paper key={idx} style={{ padding: '8px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.9)' }} elevation={2}>
                  <Typography style={{ fontSize: 12, color: '#486d86', fontWeight: 600 }}>{c.question_identifier}</Typography>
                  <Box>
                    <Typography style={{ fontSize: 14, color: mainColor, fontWeight: 700 }}>
                      {pct !== null ? `${pct}%` : (absCh !== null ? `${absCh}` : '—')}
                    </Typography>
                    {last7 !== null ? (
                      <Typography style={{ fontSize: 11, color: last7Color }}>7d: {`${last7}%`}</Typography>
                    ) : null}
                  </Box>
                  <Typography style={{ fontSize: 11, color: '#6d7993' }}>{c.note || ''}</Typography>
                </Paper>
              );
            })}
          </Box>
        </CardContent>
      )}*/}

      {/* Baseline self-rated pain items (if available) */}
      {/*{baselineSelfRated && baselineSelfRated.items && Array.isArray(baselineSelfRated.items) && baselineSelfRated.items.length > 0 && (
        <CardContent style={{ paddingTop: 0 }}>
          <Box style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
            {baselineSelfRated.items.map((it, idx) => (
              <Paper key={idx} style={{ padding: '8px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.95)' }} elevation={1}>
                <Typography style={{ fontSize: 12, color: '#486d86', fontWeight: 600 }}>Baseline</Typography>
                <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 700 }}>
                  {it.numeric_value !== null && it.numeric_value !== undefined ? `${it.numeric_value}` : (it.text_value || it.answerId || '—')}
                </Typography>
                {it.text_value ? <Typography style={{ fontSize: 11, color: '#6d7993' }}>{it.text_value}</Typography> : null}
              </Paper>
            ))}
          </Box>
        </CardContent>
      )}*/}
      <CardContent>
        <Grid container spacing={2} style={{ marginTop: 0 }}>
          {/* Phase progress — DevelopmentView-style dataPointCard with recent session grid */}
          <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
            <Paper elevation={0} style={{ borderRadius: 12, background: 'linear-gradient(180deg,#fff,#f8fbff)', border: '1px solid rgba(0,45,85,0.12)', boxShadow: '0 3px 10px rgba(0,45,85,0.08)', padding: 10, height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Header row: label + pill + View session data button */}
              <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
                <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(11px, 0.95vw, 15px)' }}>Phase progress</Typography>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Box component="span" style={{ padding: '2px 8px', borderRadius: 999, fontWeight: 900, color: '#0d7a2b', background: '#dcf5e6', fontSize: 12 }}>
                    {phasePercent != null ? `${phasePercent}%` : '-'}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => { if (typeof onScrollToJourney === 'function') onScrollToJourney(); }}
                    style={{ borderColor: '#002D55', color: '#002D55', textTransform: 'none', fontWeight: 700, fontSize: 'clamp(10px, 0.84vw, 12px)', borderRadius: 10 }}
                  >
                    View session data
                  </Button>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" style={{ marginTop: 0, marginBottom: 12, gap: 15, paddingLeft: 8 }}>
                {/* Trophy column */}
                <Tooltip title={`Next level — reach compliance of ${nextThreshold}%`} arrow>
                  <Box display="flex" flexDirection="column" alignItems="center" style={{ flexShrink: 0, gap: 2 }}>
                    <Avatar style={{ background: '#fb741a', width: 26, height: 26 }}>
                      <EmojiEventsIcon style={{ color: '#fff', fontSize: 14 }} />
                    </Avatar>
                    <Typography style={{ color: '#486d86', fontSize: 'clamp(8px, 0.7vw, 10px)', fontWeight: 700, lineHeight: 1 }}>{level.name}</Typography>
                  </Box>
                </Tooltip>
                {/* Phase title + bar column */}
                <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Tooltip title={phaseProgressTooltip} arrow interactive>
                    <div>
                      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8, width: '100%', marginBottom: 4 }}>
                        <Typography style={{ fontWeight: 700, color: '#002D55', fontSize: 'clamp(8px, 0.7vw, 10px)', lineHeight: 1.2, minWidth: 0 }}>
                          {phaseLabel}
                        </Typography>
                        {phaseAnchorSummary ? (
                          <Typography style={{ color: '#486d86', fontWeight: 700, fontSize: 'clamp(8px, 0.7vw, 10px)', lineHeight: 1.2, textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {phaseAnchorSummary}
                          </Typography>
                        ) : null}
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={phasePercent || 0}
                        classes={{ colorPrimary: classes.linearTrack, barColorPrimary: classes.linearBar }}
                        style={{ height: 8, borderRadius: 6 }}
                      />
                    </div>
                  </Tooltip>
                </Box>
              </Box>
              {/* Recent sessions grid — same layout as DevelopmentView.js */}
              <Box display="flex" alignItems="center" style={{ marginBottom: 8, gap: 6, width: '100%' }}>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  <Typography style={{ margin: 0, fontSize: 'clamp(10px, 0.9vw, 13px)', fontWeight: 500, color: '#002D55' }}>
                    Recent sessions:
                  </Typography>
                  <Box display="flex" alignItems="center" style={{ gap: 2 }}>
                    <Button
                      onClick={() => setSessionWeekOffset((prev) => prev + 1)}
                      disabled={!recentSessionsWeek.canGoBack}
                      style={{ minWidth: 22, width: 22, minHeight: 'clamp(12.5px, 1.125vw, 18.25px)', height: 'clamp(12.5px, 1.125vw, 18.25px)', lineHeight: 1, borderRadius: 8, padding: 0, border: '1px solid #d9d9d9', color: '#002D55' }}
                    >
                      <ChevronLeftIcon style={{ fontSize: 10 }} />
                    </Button>
                    <Typography style={{ color: '#486d86', fontSize: 'clamp(8px, 0.7vw, 10px)', minWidth: 66, textAlign: 'left' }}>
                      {`${recentSessionsWeek.startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${recentSessionsWeek.endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                    </Typography>
                    <Button
                      onClick={() => setSessionWeekOffset((prev) => Math.max(0, prev - 1))}
                      disabled={!recentSessionsWeek.canGoForward}
                      style={{ minWidth: 22, width: 22, minHeight: 'clamp(12.5px, 1.125vw, 18.25px)', height: 'clamp(12.5px, 1.125vw, 18.25px)', lineHeight: 1, borderRadius: 8, padding: 0, border: '1px solid #d9d9d9', color: '#002D55' }}
                    >
                      <ChevronRightIcon style={{ fontSize: 10 }} />
                    </Button>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" style={{ marginLeft: 'auto', gap: 4 }}>
                  <Box style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fb741a' }} />
                  <Typography style={{ color: '#002D55', fontSize: 'clamp(8px, 0.7vw, 10px)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {`Latest: ${recentSessionsWeek.lastCompletedDate ? recentSessionsWeek.lastCompletedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}`}
                  </Typography>
                </Box>
              </Box>
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 'clamp(3px, 0.4vw, 5px)',
                  padding: 'clamp(7px, calc(0.4vw + 2px), 9px)',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 14,
                  width: '100%',
                  maxWidth: 'clamp(260px, 96%, 380px)',
                }}
              >
                {recentSessionsWeek.days.map((day) => {
                  const date = new Date(day.date);
                  const dayName = date.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase();
                  const dayNum = date.toLocaleDateString('en-GB', { day: 'numeric' });
                  const count = day.count || 0;
                  const tooltipTitle = count > 0
                    ? `You completed ${count} session${count === 1 ? '' : 's'} this day`
                    : 'No sessions completed this day';
                  return (
                    <Tooltip key={day.date} title={tooltipTitle} arrow>
                    <Box
                      style={{
                        backgroundColor: day.completed ? '#002D55' : '#fff',
                        color: day.completed ? '#fff' : '#002D55',
                        border: day.completed ? 'none' : '1px solid #ddd',
                        padding: 'clamp(2px, 0.32vw, 4px) clamp(1px, 0.22vw, 2px)',
                        borderRadius: 12,
                        minHeight: 'clamp(33px, 4.2vw, 41px)',
                        width: '100%',
                        maxWidth: 'clamp(34px, 5.1vw, 46px)',
                        justifySelf: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Typography style={{ fontSize: 'clamp(7px, 0.58vw, 8px)', fontWeight: 700, lineHeight: 1, color: day.completed ? '#fb741a' : '#002D55' }}>{dayNum}</Typography>
                      <Typography style={{ fontSize: 'clamp(6px, 0.48vw, 7px)', marginTop: 2, lineHeight: 1, color: day.completed ? '#fff' : '#002D55' }}>{dayName}</Typography>
                      {day.completed ? (
                        <Box style={{ display: 'flex', gap: 2, marginTop: 2 }}>
                          <Box style={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: '#fff' }} />
                          {count > 1 && (
                            <Box style={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: '#fff' }} />
                          )}
                        </Box>
                      ) : (
                        <Typography style={{ fontSize: 'clamp(6px, 0.48vw, 7px)', marginTop: 2, lineHeight: 1, color: '#9e9e9e' }}>-</Typography>
                      )}
                    </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
            <Tooltip title="Adherence: percentage of prescribed sessions completed. Higher is better." arrow>
              <div>
                {renderMetricCard({
                  cardKey: 'compliance',
                  label: 'Compliance with program',
                  current: overallCompliancePercent,
                  baseline: last7CompliancePercent,
                  unit: '%',
                  lowerIsBetter: false,
                  seriesData: Array.isArray(complianceWeeklySeries.values) && complianceWeeklySeries.values.length ? complianceWeeklySeries.values : aggregateToWeeks(adherenceData),
                  seriesLabels: Array.isArray(complianceWeeklySeries.labels) && complianceWeeklySeries.labels.length ? complianceWeeklySeries.labels : weekLabels,
                  lineColor: '#2f6fed',
                  lineBg: 'rgba(47,111,237,0.12)',
                  yMax: 100,
                })}
              </div>
            </Tooltip>
          </Grid>
                        {goalMetricCards.length > 0 ? goalMetricCards.map((goal) => (
                          <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={goal.key}>
                            <Tooltip title="Patient Specific Functional Scale (PSFS). Self-reported function score (0-100). Higher is better." arrow>
                              <div>
                                {renderMetricCard({
                                  cardKey: goal.key,
                                  label: `Functional goal (PSFS): ${goal.label}`,
                                  current: goal.current,
                                  baseline: goal.baseline,
                                  unit: '/100',
                                  lowerIsBetter: false,
                                  seriesData: weeklyFunction,
                                  seriesLabels: weekLabels,
                                  lineColor: '#00a152',
                                  lineBg: 'rgba(0,161,82,0.12)',
                                })}
                              </div>
                            </Tooltip>
                          </Grid>
                        )) : (
                          <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                            <Tooltip title="Patient Specific Functional Scale (PSFS). Self-reported function score (0-100). Higher is better." arrow>
                              <div>
                                {renderMetricCard({
                                  cardKey: 'function-default',
                                  label: 'Function (PSFS)',
                                  current: latestFunction,
                                  baseline: numFromComp(apiFunctionComp, 'baseline_value') !== null ? numFromComp(apiFunctionComp, 'baseline_value') : funcBaseline,
                                  unit: '/100',
                                  lowerIsBetter: false,
                                  seriesData: weeklyFunction,
                                  seriesLabels: weekLabels,
                                  lineColor: '#00a152',
                                  lineBg: 'rgba(0,161,82,0.12)',
                                })}
                              </div>
                            </Tooltip>
                          </Grid>
                        )}

                        <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                          <Tooltip title="Average pain during daily activities (0-100). Lower is better." arrow>
                            <div>
                              {renderMetricCard({
                                cardKey: 'pain-best',
                                label: 'Pain (at best)',
                                current: displayPainBestValue,
                                baseline: numFromComp(apiAtBestComp, 'baseline_value') !== null ? numFromComp(apiAtBestComp, 'baseline_value') : painBestBaseline,
                                unit: '/100',
                                lowerIsBetter: true,
                                seriesData: weeklyPainDaily,
                                seriesLabels: weekLabels,
                                lineColor: '#fb741a',
                                lineBg: 'rgba(251,116,26,0.12)',
                              })}
                            </div>
                          </Tooltip>
                        </Grid>

                        <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                          <Tooltip title="Worst pain reported (0-100). Lower is better." arrow>
                            <div>
                              {renderMetricCard({
                                cardKey: 'pain-worst',
                                label: 'Pain (at worst)',
                                current: displayPainWorstValue,
                                baseline: numFromComp(apiAtWorstComp, 'baseline_value') !== null ? numFromComp(apiAtWorstComp, 'baseline_value') : painWorstBaseline,
                                unit: '/100',
                                lowerIsBetter: true,
                                seriesData: weeklyPainWorst,
                                seriesLabels: weekLabels,
                                lineColor: '#d84315',
                                lineBg: 'rgba(216,67,21,0.12)',
                              })}
                            </div>
                          </Tooltip>
                        </Grid>

                        <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                          <Tooltip title="Days per week with symptoms (0-7). Lower is better." arrow>
                            <div>
                              {renderMetricCard({
                                cardKey: 'frequency',
                                label: 'Frequency of symptoms',
                                current: freqCurrentDaysWeek,
                                baseline: (() => {
                                  const v = numFromComp(apiFrequencyComp, 'baseline_value');
                                  if (v === null) return freqBaselineDaysWeek;
                                  // API value may already be days/week (1-7) or a 0-100 percent
                                  return v <= 7 ? v : Number(((v / 100) * 7).toFixed(1));
                                })(),
                                unit: '/7d',
                                lowerIsBetter: true,
                                seriesData: freqDaysWeekSeries,
                                seriesLabels: weekLabels,
                                lineColor: '#f0b700',
                                lineBg: 'rgba(240,183,0,0.18)',
                                pillScaleMax: 7,
                                yMax: 7,
                              })}
                            </div>
                          </Tooltip>
                        </Grid>

                        <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                          <Tooltip title="NPS score (0-10). Higher is better." arrow>
                            <div>
                              {renderMetricCard({
                                cardKey: 'nps',
                                label: 'NPS',
                                current: latestNps,
                                baseline: npsBaselineVal,
                                unit: '/10',
                                lowerIsBetter: false,
                                seriesData: weeklyNps,
                                seriesLabels: weekLabels,
                                lineColor: '#2f6fed',
                                lineBg: 'rgba(47,111,237,0.12)',
                                yMax: 10,
                                pillScaleMax: 10,
                              })}
                            </div>
                          </Tooltip>
                        </Grid>

                        {Array.isArray(nonGoalClinicianKpis) && nonGoalClinicianKpis.map((kpi) => {
                          const kpiLabel = kpi.label || kpi.kpi_subtype || kpi.id || 'Metric';
                          const kpiIsGoal = kpi.kpi_type === 'function_goal';
                          const kpiIsFreq = (kpi.kpi_subtype || '').toLowerCase() === 'frequency';
                          const kpiHistory = Array.isArray(kpi.history) ? kpi.history : [];
                          const kpiWeeklySeries = buildSeriesFromHistory(
                            kpiHistory.map((historyPoint) => ({
                              date: historyPoint && historyPoint.date ? historyPoint.date : null,
                              value: historyPoint && historyPoint.value !== null && historyPoint.value !== undefined
                                ? Number(historyPoint.value)
                                : null,
                            })),
                            (value) => Number(value)
                          );
                          const kpiHistValues = kpiHistory
                            .map((h) => (h && h.value !== null && h.value !== undefined) ? Number(h.value) : null);
                          const kpiHistLabels = kpiHistory
                            .map((h, i) => (h && h.date)
                              ? new Date(h.date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })
                              : `v${(h && h.version) || i + 1}`);
                          const kpiBPoint = kpiHistory.find((h) => h && h.is_active_baseline)
                            || kpiHistory.find((h) => h && h.value !== null && h.value !== undefined)
                            || (kpiHistory.length ? kpiHistory[0] : null);
                          const kpiBase = kpiBPoint ? numOrNull(kpiBPoint.value) : null;
                          const kpiCur = numOrNull(kpi.numerical_value) !== null
                            ? numOrNull(kpi.numerical_value)
                            : (kpiHistValues.length ? kpiHistValues[kpiHistValues.length - 1] : null);
                          return (
                            <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={kpi.id || kpiLabel}>
                              {renderMetricCard({
                                cardKey: String(kpi.id || kpiLabel),
                                label: `${kpiIsGoal ? 'Functional goal (PSFS): ' : ''}${kpiLabel}`,
                                current: kpiCur,
                                baseline: kpiBase,
                                unit: kpiIsFreq ? '/7d' : '/100',
                                lowerIsBetter: !kpiIsGoal,
                                seriesData: Array.isArray(kpiWeeklySeries) && kpiWeeklySeries.some((value) => value !== null && value !== undefined)
                                  ? kpiWeeklySeries
                                  : kpiHistValues,
                                seriesLabels: Array.isArray(kpiWeeklySeries) && kpiWeeklySeries.some((value) => value !== null && value !== undefined)
                                  ? weekLabels
                                  : kpiHistLabels,
                                lineColor: kpiIsGoal ? '#00a152' : '#fb741a',
                                lineBg: kpiIsGoal ? 'rgba(0,161,82,0.12)' : 'rgba(251,116,26,0.12)',
                                pillScaleMax: kpiIsFreq ? 7 : 100,
                                yMax: kpiIsFreq ? 7 : 100,
                                noDataText: 'No KPI history yet.',
                              })}
                            </Grid>
                          );
                        })}
                    {/* Full-width compare chart */}
                    <Grid item xs={12} style={{ order: -2 }}>
                      <Paper elevation={0} style={{ width: '100%', borderRadius: 12, padding: 12, position: 'relative', display: 'flex', flexDirection: 'column', border: '1px solid rgba(0,45,85,0.12)', boxShadow: '0 2px 10px rgba(0,45,85,0.07)' }}>
                        {/* Chart Title*/}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography style={{ fontWeight: 700, color: '#002D55' }}>Reported change in symptoms</Typography>
                        <Box>
                          <Button size="small" onClick={() => setWeekOffset(w => w - 1)} style={{ minWidth: 36, padding: 6 }}><ArrowLeftIcon /></Button>
                          <Typography component="span" style={{ margin: '0 8px', fontWeight: 600, color: '#486d86' }}>Week {headerWeekNumber}</Typography>
                          <Button size="small" onClick={() => setWeekOffset(w => w + 1)} style={{ minWidth: 36, padding: 6 }}><ArrowRightIcon /></Button>
                        </Box>
                      </Box>
                            
                      <Box style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', minHeight: 240, minWidth: 350 }}>
                        {/* make an explicit flexed wrapper so the chart canvas can stretch to fill the available area */}
                        <Box style={{ flex: 1, width: '100%' }}>
                          <Line ref={chartRef} data={data} options={options} />
                        </Box>
                      </Box>
                      {/* custom legend — DV-style horizontal flex-wrap ring row */}
                      <Box style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', paddingLeft: 16, paddingRight: 16 }}>
                          {data.datasets.map((ds, idx) => {
                          const lastValueRaw = Array.isArray(ds.data) && ds.data.length ? ds.data[ds.data.length - 1] : null;
                          const lastValueDisplay = (lastValueRaw === null || lastValueRaw === undefined) ? null : Math.round(lastValueRaw);
                          const labelForRing = (lastValueDisplay === null) ? '--' : `${lastValueDisplay}`;
                          const color = ds.borderColor || '#6a8caf';
                          const labelText = ds.label === 'Patient Specific Functional Scale (PSFS)'
                            ? 'Functional goal'
                            : ds.label;

                          const chart = getChart();
                          const chartComputed = chart
                            ? (typeof chart.isDatasetVisible === 'function'
                                ? chart.isDatasetVisible(idx)
                                : (chart.getDatasetMeta ? !chart.getDatasetMeta(idx).hidden : true))
                            : true;
                          const isVisible = (visibleDatasets && visibleDatasets[idx] !== undefined) ? visibleDatasets[idx] : chartComputed;

                          const onClick = () => {
                            const c = getChart();
                            // Use the same derived isVisible so the React state flip and chart
                            // flip are always in sync — avoids the !undefined inversion bug
                            // that occurred for datasets added after the initial mount.
                            const nextVisible = !isVisible;
                            setVisibleDatasets(prev => {
                              const next = prev.length > idx
                                ? [...prev]
                                : [...prev, ...new Array(idx - prev.length + 1).fill(true)];
                              next[idx] = nextVisible;
                              return next;
                            });
                            if (!c) return;
                            try {
                              if (typeof c.setDatasetVisibility === 'function') {
                                c.setDatasetVisibility(idx, nextVisible);
                              } else if (c.getDatasetMeta) {
                                const meta = c.getDatasetMeta(idx);
                                meta.hidden = !nextVisible;
                              }
                              if (typeof c.update === 'function') c.update();
                            } catch (e) { /* ignore */ }
                          };

                          return (
                            <Box
                              key={idx}
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              onClick={onClick}
                              role="button"
                              tabIndex={0}
                              onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
                              style={{
                                cursor: 'pointer',
                                opacity: isVisible ? 1 : 0.4,
                                transition: 'transform 120ms ease, opacity 120ms ease',
                              }}
                            >
                              <Box style={{
                                opacity: isVisible ? 1 : 0.35,
                                transform: isVisible ? 'scale(1.06)' : 'scale(0.96)',
                                transition: 'transform 160ms ease, opacity 160ms ease, box-shadow 160ms ease',
                                boxShadow: isVisible ? '0 6px 18px rgba(0,45,85,0.16)' : '0 2px 8px rgba(0,0,0,0.06)',
                                filter: isVisible ? 'none' : 'grayscale(100%)',
                                borderRadius: '50%',
                              }}>
                                {renderRing(color, lastValueRaw, 48, labelForRing, undefined, ds.ringMaxValue || 100)}
                              </Box>
                              <Typography style={{ marginTop: 6, color: '#486d86', fontWeight: 700, fontSize: 'clamp(10px, 0.86vw, 12px)', textAlign: 'center', maxWidth: 80 }}>{labelText}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Paper>
                    </Grid>
                  <Grid item xs={12} sm={12} style={{marginTop: 0, order: -1}}>
                  {/* Summary panel: compliance, avg sessions and effect sizes */}
                    <Paper elevation={0} style={{ background: 'rgba(255,255,255,0.98)', padding: 14, borderRadius: 10, marginBottom: 8, border: '1px solid rgba(0,45,85,0.12)', boxShadow: '0 2px 10px rgba(0,45,85,0.07)' }}>
                      <Typography style={{ fontSize: 'clamp(12px, 1.09vw, 17px)', fontWeight: 800, color: '#002D55', marginBottom: 8 }}>Summary</Typography>
                      {/* patient-facing prose */}
                      <Typography style={{ fontSize: 'clamp(11px, 1.02vw, 16px)', color: '#486d86', lineHeight: 1.45 }}>
                        Since we started tracking, you've completed <strong>{overallCompliancePercent}%</strong> of your prescribed sessions on average — about <strong>{overallSessionsAvg}/{plannedSessionsPerDay}</strong> sessions per day. Over the last 7 days you completed <strong>{last7CompliancePercent}%</strong>, averaging <strong>{last7SessionsCompleted}/{plannedSessionsPerDay}</strong> sessions per day. {last7ComplianceMsg.message}
                      </Typography>

                      <Typography style={{ fontSize: 'clamp(11px, 1.02vw, 16px)', color: '#486d86', marginTop: 10, lineHeight: 1.45 }}>
                        You scored your ability to do <strong><i>"{baseline_text ? `${baseline_text}` : ''}"</i></strong> to <strong>{latestFunction}/100</strong> — this has improved by <strong style={{ color: funcEffectOverall >= 0 ? '#00a152' : '#e53935' }}>{funcEffectOverall.toFixed(1)}%</strong> since the start (from <strong>{funcBaseline}</strong> to <strong>{latestFunction}</strong>) and by <strong style={{ color: funcEffectLast7 >= 0 ? '#00a152' : '#e53935' }}>{funcEffectLast7.toFixed(1)}%</strong> in the last 7 days (from <strong>{funcBaseline}</strong> to <strong>{funcWindowSlice.toFixed(1)}</strong>). This is a good indicator that the plan is helping you move better.
                      </Typography>

                      <Typography style={{ fontSize: 'clamp(11px, 1.02vw, 16px)', color: '#486d86', marginTop: 8, lineHeight: 1.45 }}>
                        You reported your typical pain during activity (pain at best) as <strong>{latestPain}/100</strong> — this has {painBestEffectOverall >= 0 ? 'reduced' : 'changed'} by <strong style={{ color: painBestEffectOverall >= 0 ? '#00a152' : '#e53935' }}>{painBestEffectOverall.toFixed(1)}%</strong> since the start (from <strong>{painBestBaseline}</strong> to <strong>{latestPain}</strong>) and by <strong style={{ color: painBestEffectLast7 >= 0 ? '#00a152' : '#e53935' }}>{painBestEffectLast7.toFixed(1)}%</strong> in the last 7 days (from <strong>{painBestBaseline}</strong> to <strong>{painBestWindowAvg.toFixed(1)}</strong>).
                      </Typography>

                      <Typography style={{ fontSize: 'clamp(11px, 1.02vw, 16px)', color: '#486d86', marginTop: 8, lineHeight: 1.45 }}>
                        At its worst, your pain is now <strong>{painWorstData[painWorstData.length - 1]}/100</strong> — this has {painWorstEffectOverall >= 0 ? 'reduced' : 'changed'} by <strong style={{ color: painWorstEffectOverall >= 0 ? '#00a152' : '#e53935' }}>{painWorstEffectOverall.toFixed(1)}%</strong> since the start (from <strong>{painWorstBaseline}</strong> to <strong>{painWorstData[painWorstData.length - 1]}</strong>) and by <strong style={{ color: painWorstEffectLast7 >= 0 ? '#00a152' : '#e53935' }}>{painWorstEffectLast7.toFixed(1)}%</strong> in the last 7 days (from <strong>{painWorstBaseline}</strong> to <strong>{painWorstWindowAvg.toFixed(1)}</strong>).
                      </Typography>

                      <Typography style={{ fontSize: 'clamp(11px, 1.02vw, 16px)', color: '#486d86', marginTop: 8, lineHeight: 1.45 }}>
                        Symptoms frequency is currently <strong>{latestFrequencyPercent}%</strong> — this has {freqEffectOverall >= 0 ? 'reduced' : 'changed'} by <strong style={{ color: freqEffectOverall >= 0 ? '#00a152' : '#e53935' }}>{freqEffectOverall.toFixed(1)}%</strong> since the start (from <strong>{freqBaseline}%</strong> to <strong>{latestFrequencyPercent}%</strong>) and by <strong style={{ color: freqEffectLast7 >= 0 ? '#00a152' : '#e53935' }}>{freqEffectLast7.toFixed(1)}%</strong> in the last 7 days (from <strong>{freqBaseline}%</strong> to <strong>{freqWindowAvg.toFixed(1)}%</strong>).
                      </Typography>

                      <Typography style={{ fontSize: 'clamp(11px, 1.02vw, 16px)', color: '#486d86', marginTop: 10, lineHeight: 1.45 }}>
                        On average you report <strong style={{ color: painValueColor(avgPainLast7) }}>{avgPainLast7}</strong> pain per exercise over the last 7 days (overall <strong>{avgPainOverall}</strong>). We’ll show your MSK-HQ score here once it’s available so we can track it alongside these results.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
        {/*<Box display="flex" flexWrap="wrap" gap={2}>
          <Box flexBasis={{ xs: '100%', sm: '50%' }} px={1}>
            <Typography style={{ fontWeight: 'bold', color: '#002D55', fontSize: 'clamp(13px, 1.17vw, 18px)', marginBottom: 8 }}>Sub-goals:</Typography>
            {subGoals.map((goal, idx) => (
              <Box key={goal} display="flex" alignItems="center" mb={1}>
                <Box minWidth={120} mr={2} >
                  <DiscreteSliderAdmin value={subGoalValues[idx]} onChange={(e, val) => handleSubGoalChange(idx, val)} barColor={subGoalValues[idx] === 100 ? '#90ee90' : undefined} />
                </Box>
                <span style={{ color: '#002D55', fontSize: 'clamp(10px, 0.94vw, 14px)', marginTop: -25, paddingLeft: "1%" }}>{goal}</span>
              </Box>
            ))}
            <Box display="flex" justifyContent="flex-start" mt={2}>
              <input type="text" value={newSubGoal} onChange={e => setNewSubGoal(e.target.value)} placeholder="Add sub-goal..." style={{ padding: 6, borderRadius: 8, border: '1px solid #ccc', minWidth: 120 }} />
              <Button variant="contained" color="primary" onClick={handleAddSubGoal} style={{ borderRadius: 8, background: '#e0e0e0', color: '#002D55', fontWeight: 'bold', height: 32, marginLeft: 8 }}>Add</Button>
            </Box>
          </Box>

          <Box flexBasis={{ xs: '100%', sm: '50%' }} px={1}>
            <Typography style={{ fontWeight: 'bold', color: '#002D55', fontSize: 'clamp(13px, 1.17vw, 18px)', marginBottom: 8 }}>Key symptoms to track:</Typography>
            {symptoms.map((symptom, idx) => (
              <Box key={symptom} display="flex" alignItems="center" mb={1}>
                <Box minWidth={120} mr={2}>
                  <DiscreteSliderAdmin value={symptomValues[idx]} onChange={(e, val) => handleSymptomChange(idx, val)} barColor={symptomValues[idx] === 100 ? '#90ee90' : undefined} />
                </Box>
                <span style={{ color: '#e53935', fontSize: 'clamp(10px, 0.94vw, 14px)', marginTop: -25, paddingLeft: "1%" }}>{symptom}</span>
              </Box>
            ))}
            <Box display="flex" justifyContent="flex-start" mt={2}>
              <input type="text" value={newSymptom} onChange={e => setNewSymptom(e.target.value)} placeholder="Add symptom..." style={{ padding: 6, borderRadius: 8, border: '1px solid #ccc', minWidth: 120 }} />
              <Button variant="contained" color="secondary" onClick={handleAddSymptom} style={{ borderRadius: 8, background: '#e0e0e0', color: '#002D55', fontWeight: 'bold', height: 32, marginLeft: 8 }}>Add</Button>
            </Box>
          </Box>     
        </Box>*/}


        
      </CardContent>

      <Divider />
      <Box display="flex" justifyContent="center" p={2}>
        <Button className={classes.ChangeButton} 
          startIcon={<ArrowRightIcon />} 
          endIcon={<ArrowLeftIcon />} 
          size="large" 
          variant="contained"
          onClick={handleOpenPopover}
          >
          Weekly physio check-in: update symptoms
        </Button>
        <Popover
          open={popoverOpen}
          anchorEl={popoverAnchor}
          onClose={handleClosePopover}
          anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
          transformOrigin={{ vertical: 'center', horizontal: 'center' }}
          PaperProps={{ style: { width: '90vw', maxWidth: 1100, height: '85vh', padding: 0, borderRadius: 12 } }}
        >
          <div style={{ width: '100%', height: '100%' }}>
            <iframe
              title="follow up assessment"
              src="/FollowUpAssessment_questionaire"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        </Popover>
      </Box>

      <Snackbar anchorOrigin={{ vertical: 'center', horizontal: 'center' }} open={celebrateOpen} autoHideDuration={3000} onClose={() => setCelebrateOpen(false)} message={<span style={{ display: 'flex', alignItems: 'center', color: '#00e676', fontWeight: 'bold', fontSize: 'clamp(14px, 1.4vw, 22px)' }}><CheckCircleIcon style={{ marginRight: 8, color: '#90ee90' }} />{celebrateMsg}</span>} ContentProps={{ style: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,230,118,0.15)' } }} />
    </Card>
  );
};

ChartProgress.propTypes = {
  className: PropTypes.string
};

export default ChartProgress;